// Dashboard.js
// Not: App.js tarafında Dashboard çağrısı şu şekilde olmalı:
// <Dashboard user={user} userRole={userRole} onLogout={handleLogout} />

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bell, MessageCircle, X } from "lucide-react";
import Sidebar from "./components/Sidebar";
import CustomerNavigation from "./components/CustomerNavigation";
import DashboardOverview from "./components/DashboardOverview";
import ProjectsTable from "./components/ProjectsTable";
import NewProjectModal from "./components/NewProjectModal";
import CompanyDetailModal from "./components/CompanyDetailModal";
import ProjectDetailModal from "./components/ProjectDetailModal";
import CompaniesView from "./components/CompaniesView";
import ArchivedProjectsView from "./components/ArchivedProjectsView";
import LogsView from "./components/LogsView";
import UsersView from "./components/UsersView";
import BackupStatusView from "./components/BackupStatusView";
import FilePreviewModal from "./components/FilePreviewModal";
import FilesView from "./components/FilesView";
import ExemptionDayReportView from "./components/ExemptionDayReportView";
import PostDeclaration5746CheckView from "./components/PostDeclaration5746CheckView";
import MonthlyFinancialReportsView from "./components/MonthlyFinancialReportsView";
import AnnualTaxObligationsView from "./components/AnnualTaxObligationsView";
import MessagesView from "./components/MessagesView";
import {
  createAdminNotification,
  createUserNotification,
  getRecentAdminNotifications,
  markNotificationRead,
  subscribeUserNotifications
} from "./services/notificationService";

import MonthlyProcessesView from "./components/MonthlyProcessesView";
import PortalCredentialsView from "./components/PortalCredentialsView";
import ParasutIntegrationView from "./components/ParasutIntegrationView";
import MailTasksView from "./components/MailTasksView";
import AppDialogProvider from "./components/AppDialogProvider";
import ProfileView from "./components/ProfileView";
import AnnouncementsView from "./components/AnnouncementsView";
import SupportCenterView from "./components/SupportCenterView";
import SupportManagementDashboard from "./components/SupportManagementDashboard";
import TenantMonthlyProcessesView from "./components/TenantMonthlyProcessesView";
import TeknoparkAIView from "./components/TeknoparkAIView";
import { isCustomerPortalHostname, isManagementHostname, resolveTenantId } from "./services/supportService";
import {
  createAnnouncement,
  getAnnouncements
} from "./services/announcementService";
import {
  DEFAULT_PROCESS_PROJECT_ID,
  configureMonthlyProcessTenantScope,
  getCompanyContacts,
  createProcessProject,
  getProcessProjects,
  updateProcessProject
} from "./services/monthlyProcessService";

import {
  uploadProjectFileToStorage,
  updateProjectFiles,
  deleteProjectFileFromStorage
} from "./services/fileService";

import {
  formatExcelDate,
  displayDate,
  dateInputValue,
  parseProjectDate,
  getKalanAyNumber,
  calculateKalanAySureForProject,
  calculateProjeSuresiForProject,
  getProjectStatusBadge,
  getKalanSureBadgeStyle
} from "./helpers/projectHelpers";

import {
  subscribeProjects,
  createProject,
  updateProjectById,
  requestProjectArchiveById,
  approveProjectArchiveById,
  rejectProjectArchiveById,
  getProjectBySTB,
  getProjectBySTBCompanyAndName
} from "./services/projectService";
import { subscribeCompanies } from "./services/companyService";
import { ensureAnnualTaxReminders } from "./services/annualTaxObligationService";
import TECHNOPARKS from "./data/technoparks";

import {
  getUsersList,
  createSystemUser,
  updateSystemUser,
  updateUserCompanyAssignments,
  deleteSystemUser,
  setSystemUserPassword,
  sendSystemUserPasswordResetEmail,
  generateSystemUserPasswordResetLink
} from "./services/userService";

import {
  addLog,
  subscribeActivityLogs
} from "./services/logService";

import {
  subscribePresenceUsers,
  isPresenceOnline
} from "./services/presenceService";

import {
  subscribeUnreadConversations
} from "./services/chatService";


import * as XLSX from "xlsx";

const normalizeExcelHeader = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .replace(/[?.]/g, "");

const getExcelValue = (row, possibleHeaders) => {
  const normalizedRow = Object.entries(row).reduce((acc, [key, value]) => {
    acc[normalizeExcelHeader(key)] = value;
    return acc;
  }, {});

  for (const header of possibleHeaders) {
    const value = normalizedRow[normalizeExcelHeader(header)];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const normalizeDocumentStatus = (value, fallbackHasDocument = false) => {
  const text = String(value || "").trim().toLocaleLowerCase("tr-TR");

  if (["var", "evet", "yes", "true", "1"].includes(text)) {
    return "Var";
  }

  if (["yok", "hayır", "hayir", "no", "false", "0"].includes(text)) {
    return "Yok";
  }

  return fallbackHasDocument ? "Var" : "Yok";
};

const PARASUT_OWNER_EMAIL = "omur@e-calp.com";

function Dashboard({ user, userRole, userData, onLogout, onUserDataUpdate }) {
  const isTenantPortal = isCustomerPortalHostname();
  const tenantMonthlyProcessScope = isTenantPortal ? resolveTenantId(userData) : "";
  configureMonthlyProcessTenantScope(tenantMonthlyProcessScope);
  const isAdmin = userRole === "admin";
  const canEdit = userRole === "admin" || userRole === "editor";
  const canView = userRole === "admin" || userRole === "editor" || userRole === "viewer";
  const canAssignCompanies = isAdmin || userData?.canAssignCompanies === true;
  const canMonitorBackups = isAdmin && userData?.canMonitorBackups === true;
  const isParasutOwner =
    String(user?.email || "").trim().toLowerCase() === PARASUT_OWNER_EMAIL;
  const canManageParasut =
    isParasutOwner || (isAdmin && userData?.canManageParasut === true);

  const [projects, setProjects] = useState([]);
  const [companyRecords, setCompanyRecords] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      const savedTab = localStorage.getItem("activeTab");

      if (savedTab === "messages") return "dashboard";
      if (savedTab === "customers") return "companies";
      return savedTab || "dashboard";
    }

    return "dashboard";
  });
  const [supportTicketToOpen, setSupportTicketToOpen] = useState("");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("all");
  const [, setCompanyFilter] = useState("all");
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [selectedTeknopark, setSelectedTeknopark] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [documentFilter, setDocumentFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [fileCategory, setFileCategory] = useState("Genel");
  const [archivedSearchText, setArchivedSearchText] = useState("");
  
  const defaultVisibleColumns = [
  "teknoparkAdi",
  "firmaAdi",
  "firmaKisaAdi",
  "projeStbKodu",
  "projeAdi",
  "projeSuresi",
  "kalanAySure",
  "baslangicTarihi",
  "bitisTarihi",
  "ekSureTarihi",
  "sonlandirmaTarihi",
  "onayliPersonelSayisiToplam",
  "projeKategorisi",
  "projeBitirmeBelgesi",
  "yazilimTescilBelgesi"
];

const [visibleColumns, setVisibleColumns] = useState(() => {
  if (typeof window !== "undefined") {
    const savedColumns = localStorage.getItem("visibleColumns");

    if (savedColumns) {
      return JSON.parse(savedColumns);
    }
  }

  return defaultVisibleColumns;
});
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc"
  });

  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [presenceUsers, setPresenceUsers] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [isMessagesPanelOpen, setIsMessagesPanelOpen] = useState(false);
  const [messageNotification, setMessageNotification] = useState(null);
  const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] = useState(false);
  const [messageFontSize, setMessageFontSize] = useState(() => {
    try {
      const savedSize = Number(localStorage.getItem("messageFontSize"));

      if (savedSize >= 8 && savedSize <= 18) {
        return savedSize;
      }
    } catch (error) {
      // localStorage kullanılamazsa varsayılan punto ile devam eder.
    }

    return 14;
  });
  const messagesPanelRef = useRef(null);
  const notificationPanelRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const unreadMessageKeysRef = useRef(new Set());
  const unreadMessagesInitializedRef = useRef(false);
  const messageNotificationTimerRef = useRef(null);
  const isMessagesPanelOpenRef = useRef(false);
  const [processProjects, setProcessProjects] = useState([]);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [userNotifications, setUserNotifications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [selectedProcessProjectId, setSelectedProcessProjectId] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("selectedProcessProjectId") || DEFAULT_PROCESS_PROJECT_ID;
    }

    return DEFAULT_PROCESS_PROJECT_ID;
  });
  const [previewFile, setPreviewFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [uploadTotalCount, setUploadTotalCount] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

    const [userForm, setUserForm] = useState({
      email: "",
      password: "",
      name: "",
      hireDate: "",
      role: "viewer",
      accessMode: "all",
      assignedCompanies: [],
      canAssignCompanies: false,
      canMonitorBackups: false,
      canManageParasut: false,
      processProjectAccessMode: "all",
      assignedProcessProjects: [],
      isActive: true,
      editingUserId: null
    });

  const [moduleSettings] = useState({
    monthlyProcesses: true
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "true";
    } catch (error) {
      return false;
    }
  });

  useEffect(() => {
    if (activeTab === "parasutIntegration" && !canManageParasut) {
      setActiveTab("dashboard");
    }
  }, [activeTab, canManageParasut]);
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem("themeMode") || "light";
    } catch (error) {
      return "light";
    }
  });

  const toggleThemeMode = () => {
    setThemeMode((current) => (current === "dark" ? "light" : "dark"));
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed((current) => {
      const nextValue = !current;
      try {
        localStorage.setItem("sidebarCollapsed", String(nextValue));
      } catch (error) {
        // localStorage kullanılamazsa sadece geçici state ile devam eder.
      }
      return nextValue;
    });
  };

  const visibleProcessProjects = useMemo(() => {
    if (isAdmin) return processProjects;

    const accessMode = userData?.processProjectAccessMode || "all";

    if (accessMode !== "assigned") return processProjects;

    const assignedProcessProjects = Array.isArray(userData?.assignedProcessProjects)
      ? userData.assignedProcessProjects
      : [];

    return processProjects.filter(
      (item) =>
        assignedProcessProjects.includes(item.id) ||
        item.createdByUid === user?.uid
    );
  }, [isAdmin, processProjects, userData, user]);

  const selectedProcessProject =
    visibleProcessProjects.find((item) => item.id === selectedProcessProjectId) ||
    visibleProcessProjects.find((item) => item.id === DEFAULT_PROCESS_PROJECT_ID) ||
    visibleProcessProjects[0] ||
    null;

  const loadProcessProjects = useCallback(async () => {
    if (isCustomerPortalHostname()) {
      setProcessProjects([{ id: DEFAULT_PROCESS_PROJECT_ID, name: "Aylık Süreçler", isSystemDefault: true }]);
      setSelectedProcessProjectId(DEFAULT_PROCESS_PROJECT_ID);
      return;
    }

    try {
      const data = await getProcessProjects();
      setProcessProjects(data);

      if (!data.some((item) => item.id === selectedProcessProjectId)) {
        setSelectedProcessProjectId(DEFAULT_PROCESS_PROJECT_ID);
      }
    } catch (error) {
      console.error("Süreç projeleri yüklenemedi:", error);
    }
  }, [selectedProcessProjectId]);

  const loadCompanyContacts = useCallback(async () => {
    try {
      const data = await getCompanyContacts();
      setCompanyContacts(data);
    } catch (error) {
      console.error("Müşteri firma kayıtları yüklenemedi:", error);
    }
  }, []);

  const loadAdminNotifications = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await getRecentAdminNotifications();
      setAdminNotifications(data);
    } catch (error) {
      console.error("Admin bildirimleri yüklenemedi:", error);
    }
  }, [isAdmin]);

  const getDismissedAnnouncementKey = useCallback(() => {
    return `ecalp-dismissed-announcements:${user?.uid || user?.email || "guest"}`;
  }, [user]);

  const getAnnouncementViewCountKey = useCallback(() => {
    return `ecalp-announcement-view-counts:${user?.uid || user?.email || "guest"}`;
  }, [user]);

	  const getDismissedAnnouncementIds = useCallback(() => {
	    try {
	      return JSON.parse(localStorage.getItem(getDismissedAnnouncementKey()) || "[]");
	    } catch {
	      return [];
	    }
	  }, [getDismissedAnnouncementKey]);

	  const getAnnouncementViewCounts = useCallback(() => {
	    try {
	      return JSON.parse(localStorage.getItem(getAnnouncementViewCountKey()) || "{}");
	    } catch {
	      return {};
	    }
	  }, [getAnnouncementViewCountKey]);

	  const recordAnnouncementView = useCallback((announcementId) => {
	    if (!announcementId) return;

	    try {
	      const viewCounts = getAnnouncementViewCounts();
	      const nextViewCounts = {
	        ...viewCounts,
	        [announcementId]: Number(viewCounts[announcementId] || 0) + 1
	      };

	      localStorage.setItem(getAnnouncementViewCountKey(), JSON.stringify(nextViewCounts));
	    } catch {
	      // localStorage kapalıysa sadece o oturumdaki pop-up gösterilir.
	    }
	  }, [getAnnouncementViewCountKey, getAnnouncementViewCounts]);

	  const dismissAnnouncementPopup = useCallback(() => {
	    setPopupAnnouncement(null);
	  }, []);

	  const loadAnnouncements = useCallback(async ({ showPopup = false } = {}) => {
	    try {
	      const data = await getAnnouncements();
	      const visibleAnnouncements = data.filter((announcement) => {
	        const audience = announcement.audience || "all";

	        if (isAdmin) return true;

	        return audience === "all" || audience === "users";
	      });

	      const getAnnouncementSortTime = (announcement) => {
	        const directMillis = Number(announcement?.createdAtMillis || 0);
	        if (directMillis > 0) return directMillis;

	        const timestampSeconds = Number(announcement?.createdAt?.seconds || 0);
	        if (timestampSeconds > 0) return timestampSeconds * 1000;

	        const dateText = String(announcement?.createdAtText || announcement?.date || "");
	        const dateTextMatch = dateText.match(/(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
	        const parsedDate = dateTextMatch
	          ? new Date(
	              Number(dateTextMatch[3]),
	              Number(dateTextMatch[2]) - 1,
	              Number(dateTextMatch[1]),
	              Number(dateTextMatch[4] || 0),
	              Number(dateTextMatch[5] || 0)
	            ).getTime()
	          : new Date(dateText || 0).getTime();

	        return Number.isNaN(parsedDate) ? 0 : parsedDate;
	      };

	      const sortedVisibleAnnouncements = [...visibleAnnouncements].sort((first, second) => {
	        const firstTime = getAnnouncementSortTime(first);
	        const secondTime = getAnnouncementSortTime(second);

	        return secondTime - firstTime;
	      });

	      setAnnouncements(sortedVisibleAnnouncements);

	      if (showPopup) {
	        const dismissedIds = getDismissedAnnouncementIds();
	        const viewCounts = getAnnouncementViewCounts();
	        const popupAnnouncements = sortedVisibleAnnouncements.filter((item) => {
	          if (dismissedIds.includes(item.id)) return false;

	          const popupDisplayLimit = Math.min(
	            Math.max(Number(item.popupDisplayLimit || 1), 1),
	            5
	          );
	          const viewCount = Number(viewCounts[item.id] || 0);

	          return viewCount < popupDisplayLimit;
	        });

	        if (popupAnnouncements.length > 0) {
	          popupAnnouncements.forEach((announcement) => {
	            recordAnnouncementView(announcement.id);
	          });
	          setPopupAnnouncement(popupAnnouncements);
	        }
	      }
	    } catch (error) {
	      console.error("Duyurular yüklenemedi:", error);
	    }
	  }, [getAnnouncementViewCounts, getDismissedAnnouncementIds, isAdmin, recordAnnouncementView]);

  const publishAnnouncement = async (announcementForm) => {
    await createAnnouncement({
      ...announcementForm,
      createdBy: user?.email || ""
    });
    await loadAnnouncements();
    await addActivityLog("DUYURU_YAYINLANDI", announcementForm.title || "Yeni duyuru");
  };

  const openProcessProject = (projectId) => {
    const targetProjectId = projectId || visibleProcessProjects[0]?.id || DEFAULT_PROCESS_PROJECT_ID;

    if (!isAdmin && !visibleProcessProjects.some((item) => item.id === targetProjectId)) {
      alert("Bu süreç projesini görüntüleme yetkiniz bulunmuyor.");
      return;
    }

    setSelectedProcessProjectId(targetProjectId);
    setActiveTab("monthlyProcesses");
  };

  const openMailTasks = () => {
    setActiveTab("mailTasks");
  };

  const createNewProcessProject = async () => {
    const projectName = await window.appPrompt(
      "Yeni süreç projesi adını yazın. Örn: Sözleşme Takibi, Yıllık Faaliyet Raporu",
      {
        title: "Süreç Projesi Oluştur",
        type: "info",
        confirmText: "Oluştur",
        inputPlaceholder: "Süreç projesi adı"
      }
    );

    if (!projectName) return;

    try {
      const createdProjectRef = await createProcessProject({
        name: projectName,
        createdByUid: user?.uid || "",
        createdByEmail: user?.email || ""
      });
      await addActivityLog(
        "SUREC_PROJESI_EKLENDI",
        projectName
      );
      await loadProcessProjects();
      setSelectedProcessProjectId(createdProjectRef.id);
      setActiveTab("monthlyProcesses");
      alert("Süreç projesi oluşturuldu.");
    } catch (error) {
      alert("Süreç projesi oluşturulamadı: " + error.message);
    }
  };

  const renameProcessProject = async (project) => {
    if (!isAdmin || !project?.id) return;

    const nextName = await window.appPrompt("Süreç projesi adını düzenleyin.", {
      title: "Süreç Projesi Düzenle",
      type: "info",
      confirmText: "Kaydet",
      inputPlaceholder: "Süreç projesi adı",
      defaultValue: project.name || ""
    });

    if (!nextName || nextName === project.name) return;

    try {
      await updateProcessProject({
        projectId: project.id,
        data: { name: nextName }
      });
      await addActivityLog(
        "SUREC_PROJESI_GUNCELLENDI",
        `${project.name || "-"} → ${nextName}`
      );
      await loadProcessProjects();
      alert("Süreç projesi güncellendi.");
    } catch (error) {
      alert("Süreç projesi güncellenemedi: " + error.message);
    }
  };

  const archiveProcessProject = async (project) => {
    if (!isAdmin || !project?.id || project.id === DEFAULT_PROCESS_PROJECT_ID) return;

    const confirmed = await window.appConfirm(
      `${project.name} süreç projesini menüden kaldırmak istiyor musunuz? Kayıtlar silinmez, proje pasif yapılır.`,
      {
        title: "Süreç Projesi Pasifleştir",
        type: "danger",
        confirmText: "Pasifleştir"
      }
    );

    if (!confirmed) return;

    try {
      await updateProcessProject({
        projectId: project.id,
        data: { isActive: false }
      });
      await addActivityLog(
        "SUREC_PROJESI_PASIFLESTIRILDI",
        project.name || project.id
      );

      if (selectedProcessProjectId === project.id) {
        setSelectedProcessProjectId(DEFAULT_PROCESS_PROJECT_ID);
        setActiveTab("monthlyProcesses");
      }

      await loadProcessProjects();
      alert("Süreç projesi pasifleştirildi.");
    } catch (error) {
      alert("Süreç projesi pasifleştirilemedi: " + error.message);
    }
  };

  const moveProcessProject = async (projectIndex, direction) => {
    if (!isAdmin) return;

    const targetIndex = projectIndex + direction;

    if (targetIndex < 0 || targetIndex >= processProjects.length) return;

    const currentProject = processProjects[projectIndex];
    const targetProject = processProjects[targetIndex];
    const currentOrder = Number(currentProject.order ?? projectIndex);
    const targetOrder = Number(targetProject.order ?? targetIndex);

    try {
      await Promise.all([
        updateProcessProject({
          projectId: currentProject.id,
          data: { order: targetOrder }
        }),
        updateProcessProject({
          projectId: targetProject.id,
          data: { order: currentOrder }
        })
      ]);
      await addActivityLog(
        "SUREC_PROJESI_SIRASI_GUNCELLENDI",
        `${currentProject.name || currentProject.id} ↔ ${targetProject.name || targetProject.id}`
      );

      await loadProcessProjects();
    } catch (error) {
      alert("Süreç projesi sırası güncellenemedi: " + error.message);
    }
  };

  const pageSize = 50;

  const addActivityLog = async (action, detail) => {
  try {
    await addLog({
      action,
      detail,
      userEmail: user?.email
    });
  } catch (error) {
    console.error("Log kayıt hatası:", error);
  }
  };

  const getCurrentUserDisplayName = useCallback(() => {
    return (
      userData?.name ||
      userData?.displayName ||
      userData?.fullName ||
      user?.displayName ||
      user?.email ||
      "Bir kullanıcı"
    );
  }, [user, userData]);

  const notifyTeam = useCallback(async ({
    title,
    body,
    type = "info",
    link = "dashboard",
    processId = "",
    processProjectId = ""
  }) => {
    if (!title || !body) return;

    try {
      const uniqueRecipients = new Map();

      (users || []).forEach((item) => {
        const targetUserId = item.uid || item.id;
        if (!targetUserId || item.isActive === false) return;
        uniqueRecipients.set(targetUserId, {
          targetUserId,
          targetUserEmail: item.email || ""
        });
      });

      if (user?.uid && !uniqueRecipients.has(user.uid)) {
        uniqueRecipients.set(user.uid, {
          targetUserId: user.uid,
          targetUserEmail: user.email || ""
        });
      }

      await Promise.all(
        Array.from(uniqueRecipients.values()).map((recipient) =>
          createUserNotification({
            ...recipient,
            title,
            body,
            type,
            link,
            processId,
            processProjectId,
            createdBy: user?.email || "",
            createdByUid: user?.uid || ""
          })
        )
      );
    } catch (error) {
      console.error("Bildirim gönderilemedi:", error);
    }
  }, [users, user]);


        const cardHoverStyle = {
        transform: "translateY(-6px) scale(1.01)",
        boxShadow: "0 18px 38px rgba(0,0,0,0.16)"
        };


  const readOnlyInputStyle = !canEdit
    ? {
        background: "#f9fafb",
        color: "#6b7280",
        cursor: "not-allowed"
      }
    : {};

  const formInputStyle = {
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "1px solid #ccc",
    ...readOnlyInputStyle
  };

  const detailInputStyle = {
    width: "100%",
    padding: "14px 16px",
    marginTop: 6,
    borderRadius: 12,
    border: "1px solid #dbe2ea",
    ...readOnlyInputStyle
  };

    const [form, setForm] = useState({
    companyId: "",
    branchId: "",
    firmaAdi: "",
    firmaKisaAdi: "",
    teknoparkAdi: "",
    projeStbKodu: "",
    projeAdi: "",
    argePersoneli: 0,
    yazilimPersoneli: 0,
    destekPersoneli: 0,
    baslangicTarihi: "",
    bitisTarihi: "",
    ekSureTarihi: "",
    projeKategorisi: "",
    sonlandirmaTarihi: "",
    projeBitirmeBelgesi: "",
    projeBitirmeBelgesiKodu: "",
    projeBitirmeBelgesiTarihi: "",
    yazilimTescilBelgesi: "",
    yazilimTescilTarihi: "",
    aciklamaNot: ""
  });

  const updateField = (field, value) => {
    if (!canEdit) return;

    if (field === "companyId") {
      const matchedCompany = companyMasterOptions.find((company) => company.id === value);
      setForm({
        ...form,
        companyId: value,
        branchId: "",
        firmaAdi: matchedCompany?.firmaAdi || "",
        firmaKisaAdi: matchedCompany?.firmaKisaAdi || "",
        teknoparkAdi: ""
      });
      return;
    }

    if (field === "branchId") {
      const matchedCompany = companyMasterOptions.find((company) => company.id === form.companyId);
      const matchedBranch = (matchedCompany?.branches || []).find((branch) => branch.id === value);
      setForm({
        ...form,
        branchId: value,
        teknoparkAdi: matchedBranch?.technoparkName || ""
      });
      return;
    }

    if (field === "firmaAdi") {
      const matchedCompany = (customerCompanyOptions.length ? customerCompanyOptions : firmaListesi).find(
        (company) => company.firmaAdi === value
      );

      setForm({
        ...form,
        firmaAdi: value,
        firmaKisaAdi: matchedCompany?.firmaKisaAdi || form.firmaKisaAdi || ""
      });
      return;
    }

    setForm({
      ...form,
      [field]: value
    });
  };

  const getProjects = useCallback(() => {
    return subscribeProjects((data) => {
              const activeProjects = data.filter((p) => !p.isArchived);
              const archived = data.filter((p) => p.isArchived);

              setProjects(activeProjects);
              setArchivedProjects(archived);
    });
  }, []);

const getUsers = useCallback(async () => {
  const data = await getUsersList();
  setUsers(data);
}, []);

const getActivityLogs = () => {
  return subscribeActivityLogs(setActivityLogs);
};

const getUnreadConversationKey = (conversation) => {
  const lastMessageTime =
    conversation?.lastMessageAt?.seconds ||
    conversation?.updatedAt?.seconds ||
    conversation?.lastMessageAt?.toMillis?.() ||
    conversation?.updatedAt?.toMillis?.() ||
    "";

  return `${conversation?.id || ""}-${lastMessageTime}-${conversation?.lastMessage || ""}`;
};

const getConversationNotification = useCallback((conversation) => {
  if (!conversation) return null;

  const participantInfo = conversation.participantInfo || {};
  const senderInfo = participantInfo[conversation.lastSenderId] || {};
  const senderName =
    senderInfo.name ||
    senderInfo.email ||
    (conversation.type === "global" ? "Tüm Kullanıcılar" : "Yeni mesaj");

  return {
    id: getUnreadConversationKey(conversation),
    title: conversation.type === "global" ? "Tüm Kullanıcılar" : senderName,
    body:
      conversation.type === "global"
        ? `${senderName}: ${conversation.lastMessage || "Yeni mesaj"}`
        : conversation.lastMessage || "Yeni mesajınız var."
  };
}, []);

const showMessageNotification = useCallback((notification) => {
  if (!notification) return;

  if (messageNotificationTimerRef.current) {
    clearTimeout(messageNotificationTimerRef.current);
  }

  setMessageNotification(notification);

  messageNotificationTimerRef.current = setTimeout(() => {
    setMessageNotification(null);
    messageNotificationTimerRef.current = null;
  }, 6500);

  if (
    typeof window !== "undefined" &&
    "Notification" in window &&
    window.Notification.permission === "granted"
  ) {
    try {
      new window.Notification(notification.title, {
        body: notification.body,
        icon: "/favicon.ico"
      });
    } catch (error) {
      // Tarayıcı sistem bildirimi engellerse uygulama içi bildirim zaten görünür.
    }
  }
}, []);

const openMessagesPanel = useCallback(() => {
  setIsMessagesPanelOpen(true);
  setIsNotificationsPanelOpen(false);
  setMessageNotification(null);

  if (
    typeof window !== "undefined" &&
    "Notification" in window &&
    window.Notification.permission === "default"
  ) {
    window.Notification.requestPermission().catch(() => {});
  }
}, []);

const openTaskNotification = async (notification) => {
  if (!notification.isRead) {
    await markNotificationRead(notification.id);
    setUserNotifications((current) =>
      current.map((item) =>
        item.id === notification.id ? { ...item, isRead: true } : item
      )
    );
  }

  if (notification.processProjectId) {
    setSelectedProcessProjectId(notification.processProjectId);
  }

  if (notification.link) {
    setActiveTab(notification.link);
  }

  setIsNotificationsPanelOpen(false);
};

const markTaskNotificationRead = async (notification) => {
  if (notification.isRead) return;
  await markNotificationRead(notification.id);
  setUserNotifications((current) =>
    current.map((item) =>
      item.id === notification.id ? { ...item, isRead: true } : item
    )
  );
};

const openAdminNotification = async (notification) => {
  if (!notification.isRead) {
    await markNotificationRead(notification.id);
    setAdminNotifications((current) =>
      current.map((item) =>
        item.id === notification.id ? { ...item, isRead: true } : item
      )
    );
  }

  if (notification.link) {
    setActiveTab(notification.link);
  }

  setIsNotificationsPanelOpen(false);
};

const markAllNotificationsRead = async () => {
  const unreadUserNotifications = userNotifications.filter((notification) => !notification.isRead);
  const unreadAdminNotifications = isAdmin
    ? adminNotifications.filter((notification) => !notification.isRead)
    : [];
  const unreadNotifications = [...unreadUserNotifications, ...unreadAdminNotifications];

  if (unreadNotifications.length === 0) return;

  await Promise.all(
    unreadNotifications.map((notification) => markNotificationRead(notification.id))
  );

  setUserNotifications((current) =>
    current.map((notification) => ({ ...notification, isRead: true }))
  );

  if (isAdmin) {
    setAdminNotifications((current) =>
      current.map((notification) => ({ ...notification, isRead: true }))
    );
  }
};

const saveUserPermission = async () => {
  if (!isAdmin) {
    alert("Bu işlem için admin yetkisi gerekir.");
    return;
  }

  if (!userForm.email || !userForm.password) {
    alert("E-posta adresi ve şifre giriniz.");
    return;
  }

  if (!userForm.hireDate) {
    alert("İşe giriş tarihi zorunludur.");
    return;
  }

  if (userForm.password.length < 6) {
    alert("Şifre en az 6 karakter olmalıdır.");
    return;
  }

  const safeEmail = userForm.email.trim().toLowerCase();

  try {
    const result = await createSystemUser({
      email: safeEmail,
      name: userForm.name,
      hireDate: userForm.hireDate,
      password: userForm.password,
      role: userForm.role,
      accessMode: "all",
      assignedCompanies: userForm.assignedCompanies,
      canAssignCompanies: userForm.canAssignCompanies,
      canMonitorBackups: userForm.role === "admin" && userForm.canMonitorBackups,
      canManageParasut: userForm.role === "admin" && userForm.canManageParasut,
      processProjectAccessMode: userForm.processProjectAccessMode,
      assignedProcessProjects: userForm.assignedProcessProjects
    });

    console.log("Kullanıcı oluşturma sonucu:", result.data);

    await addActivityLog(
      "KULLANICI_OLUSTURULDU",
      `${safeEmail} (${userForm.role})`
    );

    alert("Kullanıcı başarıyla oluşturuldu ve yetkisi tanımlandı.");

    setUserForm({
      email: "",
      password: "",
      name: "",
      hireDate: "",
      role: "viewer",
      accessMode: "all",
      assignedCompanies: [],
      canAssignCompanies: false,
      canMonitorBackups: false,
      canManageParasut: false,
      processProjectAccessMode: "all",
      assignedProcessProjects: [],
      isActive: true,
      editingUserId: null
    });

    getUsers();
  } catch (error) {
    console.error("Kullanıcı oluşturma hatası:", error);

    if (
      error.message?.includes("email-already-exists") ||
      error.message?.includes("already exists")
    ) {
      alert("Bu e-posta adresi zaten kayıtlı.");
    } else {
      alert("Kullanıcı oluşturulurken hata oluştu: " + error.message);
    }
  }
};

const updateUserPermission = async (userData) => {
  if (!isAdmin) {
    alert("Bu işlem için admin yetkisi gerekir.");
    return;
  }

  try {
    const targetUid = userData.uid || userData.id || userData.editingUserId;

    if (!targetUid) {
      alert("Kullanıcı UID bilgisi bulunamadı.");
      return;
    }

    if (!userData.hireDate) {
      alert("İşe giriş tarihi zorunludur.");
      return;
    }

    const newPassword = String(userData.password || "");

    if (newPassword && newPassword.length < 6) {
      alert("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }

    await updateSystemUser({
      uid: targetUid,
      name: userData.name,
      hireDate: userData.hireDate,
      role: userData.role,
      accessMode: "all",
      assignedCompanies: userData.assignedCompanies || [],
      canAssignCompanies: userData.canAssignCompanies,
      canMonitorBackups: userData.role === "admin" && userData.canMonitorBackups,
      canManageParasut: userData.role === "admin" && userData.canManageParasut,
      processProjectAccessMode: userData.processProjectAccessMode,
      assignedProcessProjects: userData.assignedProcessProjects || [],
      isActive: userData.isActive !== false
    });

    if (newPassword) {
      await setSystemUserPassword({
        uid: targetUid,
        email: userData.email,
        password: newPassword
      });
    }

    await addActivityLog(
      "KULLANICI_GUNCELLENDI",
      `${userData.email} (${userData.role})${newPassword ? " - şifre güncellendi" : ""}`
    );

    alert(newPassword ? "Kullanıcı ve şifresi güncellendi." : "Kullanıcı güncellendi.");
    getUsers();
  } catch (error) {
    alert("Kullanıcı güncellenirken hata oluştu: " + error.message);
  }
};

const sendPasswordResetToUser = async (targetUser) => {
  if (!isAdmin) {
    alert("Bu işlem için admin yetkisi gerekir.");
    return;
  }

  const email = String(targetUser?.email || "").trim().toLowerCase();

  if (!email) {
    alert("Kullanıcının e-posta adresi bulunamadı.");
    return;
  }

  try {
    await sendSystemUserPasswordResetEmail({ email });

    await addActivityLog(
      "KULLANICI_SIFRE_RESET_MAILI_GONDERILDI",
      email
    );

    alert("Şifre sıfırlama maili gönderildi. Kullanıcı gelen kutusu/spam klasörünü kontrol etsin.");
  } catch (error) {
    console.error("Şifre reset maili hatası:", error);
    alert("Şifre sıfırlama maili gönderilemedi: " + (error.message || error.code || "Bilinmeyen hata"));
  }
};

const generatePasswordResetLinkForUser = async (targetUser) => {
  if (!isAdmin) {
    alert("Bu işlem için admin yetkisi gerekir.");
    return;
  }

  const email = String(targetUser?.email || "").trim().toLowerCase();
  const uid = targetUser?.uid || targetUser?.id || "";

  if (!email && !uid) {
    alert("Kullanıcı bilgisi bulunamadı.");
    return;
  }

  try {
    const result = await generateSystemUserPasswordResetLink({ uid, email });
    const resetLink = result?.resetLink;

    if (!resetLink) {
      alert("Reset linki oluşturulamadı.");
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(resetLink);
      alert("Şifre sıfırlama linki oluşturuldu ve panoya kopyalandı.");
    } else {
      window.prompt("Şifre sıfırlama linki:", resetLink);
    }

    await addActivityLog(
      "KULLANICI_SIFRE_RESET_LINKI_OLUSTURULDU",
      result?.email || email || uid
    );
  } catch (error) {
    console.error("Şifre reset linki hatası:", error);
    alert("Şifre sıfırlama linki oluşturulamadı: " + (error.message || error.code || "Bilinmeyen hata"));
  }
};

const updateUserCompanyAssignmentsOnly = async (targetUserData) => {
  if (!canAssignCompanies) {
    alert("Firma ataması yapmak için yetkiniz bulunmuyor.");
    return;
  }

  try {
    const targetUid = targetUserData.uid || targetUserData.id || targetUserData.editingUserId;

    if (!targetUid) {
      alert("Kullanıcı UID bilgisi bulunamadı.");
      return;
    }

    await updateUserCompanyAssignments({
      uid: targetUid,
      assignedCompanies: targetUserData.assignedCompanies || []
    });

    await addActivityLog(
      "KULLANICI_FIRMA_ATAMASI_GUNCELLENDI",
      `${targetUserData.email || targetUid} için ${(targetUserData.assignedCompanies || []).length} firma atandı`
    );

    alert("Firma ataması güncellendi.");
    getUsers();
  } catch (error) {
    alert("Firma ataması güncellenirken hata oluştu: " + error.message);
  }
};

const removeUserPermission = async (email) => {
  if (!isAdmin) return;

  const confirmDelete = await window.appConfirm(
    "Bu kullanıcıyı hem Authentication hem de yetki listesinden silmek istediğinizden emin misiniz?",
    { title: "Kullanıcı silinsin mi?", type: "danger", confirmText: "Sil" }
  );

  if (!confirmDelete) return;

  try {
    await deleteSystemUser(email);

    await addActivityLog(
      "KULLANICI_SILINDI",
      email
    );

    alert("Kullanıcı başarıyla silindi.");
    getUsers();
  } catch (error) {
    console.error(error);
    alert("Kullanıcı silinirken hata oluştu: " + error.message);
  }
};

  const calculateTotalPersonel = () => {
    return (
      Number(form.argePersoneli || 0) +
      Number(form.yazilimPersoneli || 0) +
      Number(form.destekPersoneli || 0)
    );
  };

  const calculateKalanAySure = () => {
    return calculateKalanAySureForProject(form);
  };

    const saveProject = async () => {
    if (!canEdit) {
      alert("Bu işlem için yetkiniz bulunmuyor.");
      return;
    }

    if (companyMasterOptions.length > 0 && (!form.companyId || !form.branchId)) {
      alert("Proje için firma ve teknopark şubesi seçilmelidir.");
      return;
    }

    const finalProjeBitirmeBelgesi =
      form.sonlandirmaTarihi && !form.projeBitirmeBelgesi
        ? "Kontrol Edilecek"
        : form.projeBitirmeBelgesi;

    const finalYazilimTescilBelgesi =
      form.projeKategorisi === "Yazılım" && !form.yazilimTescilBelgesi
        ? "Kontrol Edilecek"
        : form.yazilimTescilBelgesi;

    const masterCompany = companyMasterOptions.find((company) => company.id === form.companyId);
    const existingCompany = firmaListesi.find(
      (firma) => normalizeEntityName(firma.firmaAdi) === normalizeEntityName(form.firmaAdi)
    );

    const existingTeknopark = teknoparkOptions.find(
      (teknopark) =>
        teknopark !== "all" &&
        normalizeEntityName(teknopark) === normalizeEntityName(form.teknoparkAdi)
    );

    const finalFirmaAdi = masterCompany?.firmaAdi || existingCompany?.firmaAdi || form.firmaAdi.trim();
    const finalTeknoparkAdi = existingTeknopark || form.teknoparkAdi.trim();
    const finalFirmaKisaAdi =
      form.firmaKisaAdi?.trim() ||
      masterCompany?.firmaKisaAdi ||
      existingCompany?.firmaKisaAdi ||
      getAutoFirmaKisaAdi(finalFirmaAdi);

    await createProject({
      ...form,
      firmaAdi: finalFirmaAdi,
      firmaKisaAdi: finalFirmaKisaAdi,
      teknoparkAdi: finalTeknoparkAdi,
      projeBitirmeBelgesi: finalProjeBitirmeBelgesi,
      yazilimTescilBelgesi: finalYazilimTescilBelgesi,
      argePersoneli: Number(form.argePersoneli || 0),
      yazilimPersoneli: Number(form.yazilimPersoneli || 0),
      destekPersoneli: Number(form.destekPersoneli || 0),
      onayliPersonelSayisiToplam: calculateTotalPersonel(),
      kalanAySure: calculateKalanAySure()
    });


    await addActivityLog(
      "PROJE_EKLENDI",
      `${finalFirmaAdi} - ${form.projeAdi}`
      );

    await notifyTeam({
      title: "Yeni proje eklendi",
      body: `${getCurrentUserDisplayName()}, ${finalFirmaAdi} - ${form.projeAdi} projesini ekledi.`,
      type: "project",
      link: "projects"
    });

    alert("Proje başarıyla kaydedildi.");
    setShowNewProjectModal(false);
  };

  const updateProject = async (overrideProject = null, options = {}) => {
    if (!canEdit) {
      alert("Bu işlem için yetkiniz bulunmuyor.");
      return;
    }

    const projectToSave = overrideProject || editProject;

    if (!projectToSave?.id) return;

    await updateProjectById(projectToSave.id, {
      ...projectToSave,
      argePersoneli: Number(projectToSave.argePersoneli || 0),
      yazilimPersoneli: Number(projectToSave.yazilimPersoneli || 0),
      destekPersoneli: Number(projectToSave.destekPersoneli || 0),
      onayliPersonelSayisiToplam:
        Number(projectToSave.argePersoneli || 0) +
        Number(projectToSave.yazilimPersoneli || 0) +
        Number(projectToSave.destekPersoneli || 0),
      kalanAySure: calculateKalanAySureForProject(projectToSave)
    });

      await addActivityLog(
        "PROJE_GUNCELLENDI",
        `${projectToSave.firmaAdi} - ${projectToSave.projeAdi}`
      );

    if (!options.silent) {
      await notifyTeam({
        title: "Proje güncellendi",
        body: `${getCurrentUserDisplayName()}, ${projectToSave.firmaAdi} - ${projectToSave.projeAdi} projesini güncelledi.`,
        type: "project",
        link: "projects"
      });
    }

    if (!options.silent) {
      alert("Proje güncellendi.");
      setSelectedProject(projectToSave);
      setEditProject(projectToSave);

      if (options.closeAfterSave) {
        setSelectedProject(null);
        setEditProject(null);
      }
    }
  };

  const requestProjectArchive = async () => {
    if (!canEdit) {
      alert("Bu işlem için yetkiniz bulunmuyor.");
      return;
    }

    if (!selectedProject?.id) return;

    if (selectedProject.archiveApprovalStatus === "pending") {
      alert("Bu proje için zaten admin onayı bekleyen bir arşivleme talebi var.");
      return;
    }

    const archiveReason = String(await window.appPrompt(
      `${selectedProject.firmaAdi} - ${selectedProject.projeAdi} projesinin neden arşivlenmesi gerektiğini açıklayın. Gerekçe admin onayına gönderilecektir.`,
      {
        title: "Arşivleme gerekçesi",
        type: "warning",
        confirmText: "Onaya Gönder",
        inputPlaceholder: "Arşivleme nedenini en az 10 karakterle açıklayın",
        multiline: true
      }
    ) || "").trim();

    if (!archiveReason) return;

    if (archiveReason.length < 10) {
      alert("Arşivleme gerekçesi en az 10 karakter olmalıdır.");
      return;
    }

    try {
      await requestProjectArchiveById(selectedProject.id, {
        reason: archiveReason,
        requestedBy: user?.email || "",
        requestedByUid: user?.uid || ""
      });

      await addActivityLog(
        "PROJE_ARSIV_TALEBI_OLUSTURULDU",
        `${selectedProject.firmaAdi} - ${selectedProject.projeAdi} | Gerekçe: ${archiveReason}`
      );

      await createAdminNotification({
        title: "Proje arşivleme onayı bekliyor",
        body: `${getCurrentUserDisplayName()}, ${selectedProject.firmaAdi} - ${selectedProject.projeAdi} projesi için arşivleme talebi oluşturdu. Gerekçe: ${archiveReason}`,
        type: "warning",
        link: "archived",
        createdBy: user?.email || ""
      });

      alert("Arşivleme talebi admin onayına gönderildi.");
      setSelectedProject(null);
      setEditProject(null);
    } catch (error) {
      console.error("Proje arşivleme talebi oluşturulamadı:", error);
      alert(`Arşivleme talebi oluşturulamadı: ${error.message || "Bilinmeyen hata"}`);
    }
  };

  const notifyArchiveRequester = async (project, { title, body, type }) => {
    const requesterEmail = String(project?.archiveRequestedBy || "").trim().toLowerCase();
    const requester = users.find((item) =>
      String(item.email || "").trim().toLowerCase() === requesterEmail
    );

    if (!requester) return;

    await createUserNotification({
      targetUserId: requester.uid || requester.id,
      targetUserEmail: requester.email || "",
      title,
      body,
      type,
      link: "archived",
      createdBy: user?.email || "",
      createdByUid: user?.uid || ""
    });
  };

  const approveProjectArchive = async (project) => {
    if (!isAdmin || !project?.id) return;

    const confirmed = await window.appConfirm(
      `${project.firmaAdi} - ${project.projeAdi} projesinin arşivleme talebini onaylıyor musunuz?\n\nGerekçe: ${project.archiveReason || "-"}`,
      {
        title: "Arşivleme talebini onayla",
        type: "danger",
        confirmText: "Onayla ve Arşivle"
      }
    );

    if (!confirmed) return;

    try {
      await approveProjectArchiveById(project.id, {
        approvedBy: user?.email || "",
        approvedByUid: user?.uid || ""
      });

      await addActivityLog(
        "PROJE_ARSIV_TALEBI_ONAYLANDI",
        `${project.firmaAdi} - ${project.projeAdi} | Talep eden: ${project.archiveRequestedBy || "-"}`
      );

      await notifyArchiveRequester(project, {
        title: "Proje arşivleme talebi onaylandı",
        body: `${project.firmaAdi} - ${project.projeAdi} projesi admin tarafından arşivlendi.`,
        type: "success"
      });

      alert("Arşivleme talebi onaylandı ve proje arşive taşındı.");
    } catch (error) {
      console.error("Proje arşivleme onayı başarısız:", error);
      alert(`Arşivleme talebi onaylanamadı: ${error.message || "Bilinmeyen hata"}`);
    }
  };

  const rejectProjectArchive = async (project) => {
    if (!isAdmin || !project?.id) return;

    const rejectionReason = String(await window.appPrompt(
      `${project.firmaAdi} - ${project.projeAdi} projesinin arşivleme talebini neden reddettiğinizi yazın.`,
      {
        title: "Arşivleme talebini reddet",
        type: "danger",
        confirmText: "Talebi Reddet",
        inputPlaceholder: "Red nedenini en az 5 karakterle açıklayın",
        multiline: true
      }
    ) || "").trim();

    if (!rejectionReason) return;

    if (rejectionReason.length < 5) {
      alert("Red nedeni en az 5 karakter olmalıdır.");
      return;
    }

    try {
      await rejectProjectArchiveById(project.id, {
        rejectedBy: user?.email || "",
        rejectedByUid: user?.uid || "",
        rejectionReason
      });

      await addActivityLog(
        "PROJE_ARSIV_TALEBI_REDDEDILDI",
        `${project.firmaAdi} - ${project.projeAdi} | Red nedeni: ${rejectionReason}`
      );

      await notifyArchiveRequester(project, {
        title: "Proje arşivleme talebi reddedildi",
        body: `${project.firmaAdi} - ${project.projeAdi} projesi için talebiniz reddedildi. Neden: ${rejectionReason}`,
        type: "warning"
      });

      alert("Arşivleme talebi reddedildi.");
    } catch (error) {
      console.error("Proje arşivleme talebi reddedilemedi:", error);
      alert(`Arşivleme talebi reddedilemedi: ${error.message || "Bilinmeyen hata"}`);
    }
  };


  const deleteProjectFile = async (fileIndex) => {
  if (!canEdit) {
    alert("Bu işlem için yetkiniz bulunmuyor.");
    return;
  }

  if (!editProject?.id) return;

  const confirmDelete = await window.appConfirm(
    "Bu dosyayı silmek istediğinizden emin misiniz?",
    { title: "Dosya silinsin mi?", type: "danger", confirmText: "Sil" }
  );

  if (!confirmDelete) return;

  try {
    const fileToDelete = editProject.files[fileIndex];

    await deleteProjectFileFromStorage(fileToDelete.path);

    const updatedFiles = editProject.files.filter(
      (_, index) => index !== fileIndex
    );

    await updateProjectFiles(editProject.id, updatedFiles);

    await addActivityLog(
      "DOSYA_SILINDI",
      `${editProject.firmaAdi} - ${fileToDelete.name}`
    );

    await notifyTeam({
      title: "Proje dosyası silindi",
      body: `${getCurrentUserDisplayName()}, ${editProject.firmaAdi} - ${editProject.projeAdi} projesinden ${fileToDelete.name} dosyasını sildi.`,
      type: "file",
      link: "files"
    });

    setEditProject({
      ...editProject,
      files: updatedFiles
    });

    setSelectedProject({
      ...selectedProject,
      files: updatedFiles
    });

    alert("Dosya başarıyla silindi.");
  } catch (error) {
    console.error("Dosya silme hatası:", error);
    alert("Dosya silinirken hata oluştu: " + error.message);
  }
};

const deleteProjectFileFromFilesView = async (file) => {
  if (!canEdit) {
    alert("Bu işlem için yetkiniz bulunmuyor.");
    return;
  }

  const confirmDelete = await window.appConfirm(
    `"${file.name}" dosyasını silmek istediğinizden emin misiniz?`,
    { title: "Dosya silinsin mi?", type: "danger", confirmText: "Sil" }
  );

  if (!confirmDelete) return;

  try {
    await deleteProjectFileFromStorage(file.path);

    const project = projects.find((p) => p.id === file.projectId);

    if (!project) {
      alert("Proje bulunamadı.");
      return;
    }

    const updatedFiles = (project.files || []).filter(
      (f) => f.path !== file.path
    );

    await updateProjectFiles(project.id, updatedFiles);

    await addActivityLog(
      "DOSYA_SILINDI",
      `${project.firmaAdi} - ${file.name}`
    );

    await notifyTeam({
      title: "Proje dosyası silindi",
      body: `${getCurrentUserDisplayName()}, ${project.firmaAdi} - ${project.projeAdi} projesinden ${file.name} dosyasını sildi.`,
      type: "file",
      link: "files"
    });

    alert("Dosya başarıyla silindi.");
  } catch (error) {
    console.error("Dosya silme hatası:", error);
    alert("Dosya silinirken hata oluştu: " + error.message);
  }
};

const uploadProjectFile = async (e) => {
  if (!canEdit) {
    alert("Dosya yükleme işlemi için yetkiniz bulunmuyor.");
    e.target.value = "";
    return;
  }

  const files = Array.from(e.target.files || []);

  if (!files.length || !selectedProject?.id) return;

  const allowedTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "image/jpeg",
    "image/png"
  ];

  const maxFileSize = 10 * 1024 * 1024;

  setIsUploadingFile(true);
  setUploadProgress(0);
  setUploadingFileName("");
  setUploadedCount(0);
  setUploadTotalCount(files.length);

  try {
    const currentFiles = editProject?.files || [];
    const uploadedFiles = [];

    for (const file of files) {

      setUploadingFileName(file.name);

      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name} desteklenmeyen dosya türü.`);
        continue;
      }

      if (file.size > maxFileSize) {
        alert(`${file.name} dosyası 10 MB sınırını aşıyor.`);
        continue;
      }

      const newFile = await uploadProjectFileToStorage({
        projectId: selectedProject.id,
        file,
        category: fileCategory,
        userEmail: user?.email || "",
        onProgress: (fileProgress) => {
          setUploadProgress(
            Math.round(((uploadedFiles.length + fileProgress / 100) / files.length) * 100)
          );
        }
      });

      uploadedFiles.push(newFile);

      const currentUploaded = uploadedFiles.length;

      setUploadedCount(currentUploaded);

      setUploadProgress(
        Math.round((currentUploaded / files.length) * 100)
      );

      await addActivityLog(
        "DOSYA_YUKLENDI",
        `${selectedProject.firmaAdi} - ${selectedProject.projeAdi} / ${file.name}`
      );
    }

    if (uploadedFiles.length === 0) {
      e.target.value = "";
      return;
    }

    const updatedFiles = [...currentFiles, ...uploadedFiles];

    await updateProjectFiles(selectedProject.id, updatedFiles);

    await notifyTeam({
      title: uploadedFiles.length === 1 ? "Proje dosyası yüklendi" : "Proje dosyaları yüklendi",
      body: `${getCurrentUserDisplayName()}, ${selectedProject.firmaAdi} - ${selectedProject.projeAdi} projesine ${uploadedFiles.length} dosya yükledi.`,
      type: "file",
      link: "files"
    });

    setEditProject({
      ...editProject,
      files: updatedFiles
    });

    setSelectedProject({
      ...selectedProject,
      files: updatedFiles
    });

    alert(`${uploadedFiles.length} dosya başarıyla yüklendi.`);
    e.target.value = "";
    } catch (error) {
    console.error("Dosya yükleme hatası:", error);
    alert("Dosya yüklenirken hata oluştu: " + error.message);
  } finally {
      setTimeout(() => {
      setIsUploadingFile(false);
      setUploadProgress(0);
      setUploadingFileName("");
      setUploadTotalCount(0);
      setUploadedCount(0);
        }, 800);
    }
};

    useEffect(() => {
      const unsubscribeProjects = getProjects();
      const unsubscribeLogs = getActivityLogs();
      const unsubscribeCompanies = subscribeCompanies(setCompanyRecords);

      getUsers();

      return () => {
        if (unsubscribeProjects) {
          unsubscribeProjects();
        }

        if (unsubscribeLogs) {
          unsubscribeLogs();
        }

        if (unsubscribeCompanies) {
          unsubscribeCompanies();
        }
      };
    }, [getProjects, getUsers]);

    useEffect(() => {
      if (!user?.uid) return undefined;

      return subscribePresenceUsers(setPresenceUsers);
    }, [user?.uid]);

    useEffect(() => {
      if (!user?.uid) return undefined;

      unreadMessageKeysRef.current = new Set();
      unreadMessagesInitializedRef.current = false;

      return subscribeUnreadConversations(user.uid, (unreadConversations) => {
        setUnreadMessageCount(unreadConversations.length);

        const nextUnreadKeys = new Set(
          unreadConversations.map(getUnreadConversationKey)
        );

        if (!unreadMessagesInitializedRef.current) {
          unreadMessageKeysRef.current = nextUnreadKeys;
          unreadMessagesInitializedRef.current = true;
          return;
        }

        const newUnreadConversation = unreadConversations.find(
          (conversation) =>
            !unreadMessageKeysRef.current.has(getUnreadConversationKey(conversation))
        );

        unreadMessageKeysRef.current = nextUnreadKeys;

        if (newUnreadConversation && !isMessagesPanelOpenRef.current) {
          showMessageNotification(
            getConversationNotification(newUnreadConversation)
          );
        }
      });
    }, [getConversationNotification, showMessageNotification, user?.uid]);

    useEffect(() => {
      isMessagesPanelOpenRef.current = isMessagesPanelOpen;

      if (isMessagesPanelOpen) {
        setMessageNotification(null);
      }
    }, [isMessagesPanelOpen]);

    useEffect(() => {
      return () => {
        if (messageNotificationTimerRef.current) {
          clearTimeout(messageNotificationTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      loadProcessProjects();
    }, [loadProcessProjects]);

    useEffect(() => {
      loadCompanyContacts();
    }, [loadCompanyContacts]);

	    useEffect(() => {
	      loadAdminNotifications();
	    }, [loadAdminNotifications]);

	    useEffect(() => {
	      if (!user?.uid && !user?.email) return;

	      loadAnnouncements({ showPopup: true });
	    }, [loadAnnouncements, user?.email, user?.uid]);

    useEffect(() => {
      if (!user?.uid) return undefined;

      return subscribeUserNotifications(user.uid, setUserNotifications);
    }, [user?.uid]);

    useEffect(() => {
      if (!isNotificationsPanelOpen) return undefined;

      const closeOnOutsideClick = (event) => {
        const target = event.target;

        if (
          notificationPanelRef.current?.contains(target) ||
          notificationButtonRef.current?.contains(target)
        ) {
          return;
        }

        setIsNotificationsPanelOpen(false);
      };

      const closeOnEscape = (event) => {
        if (event.key === "Escape") {
          setIsNotificationsPanelOpen(false);
        }
      };

      document.addEventListener("mousedown", closeOnOutsideClick);
      document.addEventListener("touchstart", closeOnOutsideClick);
      window.addEventListener("keydown", closeOnEscape);

      return () => {
        document.removeEventListener("mousedown", closeOnOutsideClick);
        document.removeEventListener("touchstart", closeOnOutsideClick);
        window.removeEventListener("keydown", closeOnEscape);
      };
    }, [isNotificationsPanelOpen]);

    useEffect(() => {
      if (visibleProcessProjects.length === 0) return;

      if (!visibleProcessProjects.some((item) => item.id === selectedProcessProjectId)) {
        setSelectedProcessProjectId(visibleProcessProjects[0].id);
      }
    }, [selectedProcessProjectId, visibleProcessProjects]);

    useEffect(() => {
      localStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    useEffect(() => {
      localStorage.setItem("activeTab", activeTab);
      setIsNotificationsPanelOpen(false);
    }, [activeTab]);

    useEffect(() => {
      if (!isCustomerPortalHostname() || !user?.uid) return;
      const tenantId = resolveTenantId(userData);
      if (!tenantId) return;
      ensureAnnualTaxReminders({ tenantId, user }).catch((reminderError) => {
        console.error("Yıllık yükümlülük bildirimleri hazırlanamadı:", reminderError);
      });
    }, [user, userData]);

    useEffect(() => {
      localStorage.setItem("selectedProcessProjectId", selectedProcessProjectId);
    }, [selectedProcessProjectId]);

    useEffect(() => {
      if (activeTab === "backupStatus" && !canMonitorBackups) {
        setActiveTab("dashboard");
      }
    }, [activeTab, canMonitorBackups]);

    useEffect(() => {
      try {
        localStorage.setItem("messageFontSize", String(messageFontSize));
      } catch (error) {
        // localStorage kullanılamazsa sadece oturum içinde korunur.
      }
    }, [messageFontSize]);

    useEffect(() => {
      try {
        localStorage.setItem("themeMode", themeMode);
      } catch (error) {
        // localStorage kullanılamazsa sadece oturum içinde tema korunur.
      }

      document.documentElement.setAttribute("data-theme", themeMode);
      document.body.setAttribute("data-theme", themeMode);
    }, [themeMode]);

    useEffect(() => {
      document.body.classList.toggle("aurora-customer-portal", isTenantPortal);

      return () => {
        document.body.classList.remove("aurora-customer-portal");
      };
    }, [isTenantPortal]);

    useEffect(() => {
        const handleEsc = (e) => {
          if (e.key === "Escape") {
            setSelectedProject(null);
            setEditProject(null);
            setSelectedCompany(null);
            setShowNewProjectModal(false);
            setIsMessagesPanelOpen(false);
          }
        };

        window.addEventListener("keydown", handleEsc);

        return () => {
          window.removeEventListener("keydown", handleEsc);
        };
      }, []);

        useEffect(() => {
          const hasOpenModal =
            selectedProject || selectedCompany || showNewProjectModal;

          if (hasOpenModal) {
            document.body.style.overflow = "hidden";
          } else {
            document.body.style.overflow = "auto";
          }

          return () => {
            document.body.style.overflow = "auto";
          };
        }, [selectedProject, selectedCompany, showNewProjectModal]);

  const degerlendirmeBekleyenProjeler = projects.filter(
    (p) => p.projeStbKodu?.toUpperCase() === "DB"
  );

  const firmaBazliSonProjeler = Object.values(
    projects.reduce((acc, project) => {
      if (!project.firmaAdi) return acc;

      const hedefTarih = project.ekSureTarihi || project.bitisTarihi;
      if (!hedefTarih) return acc;

      const hedefDate = parseProjectDate(hedefTarih);
      if (!hedefDate) return acc;

      const mevcut = acc[project.firmaAdi];

      if (!mevcut) {
        acc[project.firmaAdi] = project;
        return acc;
      }

      const mevcutTarih = mevcut.ekSureTarihi || mevcut.bitisTarihi;
      const mevcutDate = parseProjectDate(mevcutTarih);

      if (!mevcutDate || hedefDate > mevcutDate) {
        acc[project.firmaAdi] = project;
      }

      return acc;
    }, {})
  );

    const projeBitmesineYakin = projects
    .filter((p) => {
      if (p.projeStbKodu?.toUpperCase() === "DB") return false;
      if (p.sonlandirmaTarihi) return false;

      const kalanGun = getKalanAyNumber(p);

      if (kalanGun === null) return false;

      return kalanGun <= 90;
    })
    .sort((a, b) => getKalanAyNumber(a) - getKalanAyNumber(b));

  const isDateBeforeToday = (value) => {
    const date = parseProjectDate(value);
    if (!date) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    return date < today;
  };

  const sonlandirmaGerekenProjeler = projects.filter((p) => {
    if (p.projeStbKodu?.toUpperCase() === "DB") return false;
    if (p.sonlandirmaTarihi) return false;

    const takipTarihi = p.ekSureTarihi || p.bitisTarihi;

    return isDateBeforeToday(takipTarihi);
  });

  const yazilimTescilGerekenProjeler = firmaBazliSonProjeler.filter(
    (p) =>
      p.projeKategorisi === "Yazılım" &&
      p.yazilimTescilBelgesi !== "Var"
  );

  const bitirmeBelgesiAlinacakProjeler = projects.filter((p) => {
    if (p.projeStbKodu?.toUpperCase() === "DB") return false;
    if (!p.sonlandirmaTarihi) return false;

    const bitirmeBelgesiYok =
      !p.projeBitirmeBelgesi ||
      p.projeBitirmeBelgesi === "Yok" ||
      p.projeBitirmeBelgesi === "Kontrol Edilecek";

    return bitirmeBelgesiYok && !p.projeBitirmeBelgesiTarihi;
  });

const toplamPersonel = projects.reduce(
  (total, project) => total + Number(project.onayliPersonelSayisiToplam || 0),
  0
);

const suresiGecenProjeler = projects.filter((p) => {
  const kalanGun = getKalanAyNumber(p);
  return kalanGun !== null && kalanGun < 0;
});

const getAutoFirmaKisaAdi = (firmaAdi = "") => {
  return String(firmaAdi)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join(" ");
};

const normalizeEntityName = (value = "") =>
  String(value)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("İ", "i")
    .replace(/\s+/g, " ");

const firmaListesi = Object.values(
  projects.reduce((acc, project) => {
    const firma = project.firmaAdi || "Firma Belirtilmemiş";

    const firmaKisaAdi =
    project.firmaKisaAdi ||
    project.companyShortName ||
    project.kisaAd ||
    getAutoFirmaKisaAdi(firma);

   if (!acc[firma]) {
      acc[firma] = {
        firmaAdi: firma,
        firmaKisaAdi,
        projeSayisi: 0,
        aktifProjeSayisi: 0,
        sonlandirilanProjeSayisi: 0,
        bitirmeBelgesiSayisi: 0,
        tamamlananProjeSayisi: 0,
        riskliProjeSayisi: 0,
        kritikProjeSayisi: 0,
        projeler: []
      };
    }

    acc[firma].projeSayisi += 1;
    acc[firma].projeler.push(project);

    const kalanGun = getKalanAyNumber(project);

      if (kalanGun !== null) {
        if (kalanGun < 0) {
          acc[firma].kritikProjeSayisi += 1;
        } else if (kalanGun <= 90) {
          acc[firma].riskliProjeSayisi += 1;
        }
      }

    if (project.sonlandirmaTarihi) {
      acc[firma].sonlandirilanProjeSayisi += 1;
    } else {
      acc[firma].aktifProjeSayisi += 1;
    }

    if (
      project.projeBitirmeBelgesi === "Var" &&
      project.projeBitirmeBelgesiKodu
    ) {
      acc[firma].bitirmeBelgesiSayisi += 1;
    }

    if (project.sonlandirmaTarihi) {
      acc[firma].tamamlananProjeSayisi += 1;
    }

    return acc;
  }, {})
  ).sort((a, b) =>
  String(a.firmaKisaAdi || a.firmaAdi || "").localeCompare(
    String(b.firmaKisaAdi || b.firmaAdi || ""),
    "tr"
  )
  );

const customerCompanyOptions = Object.values(
  companyContacts.reduce((acc, contact) => {
    const firmaAdi = contact.companyName || "";
    if (!firmaAdi) return acc;

    if (!acc[firmaAdi]) {
      acc[firmaAdi] = {
        firmaAdi,
        firmaKisaAdi: contact.companyShortName || "",
        projeSayisi: 0,
        projeler: []
      };
    }

    if (!acc[firmaAdi].firmaKisaAdi && contact.companyShortName) {
      acc[firmaAdi].firmaKisaAdi = contact.companyShortName;
    }

    return acc;
  }, {})
).sort((a, b) =>
  String(a.firmaKisaAdi || a.firmaAdi || "").localeCompare(
    String(b.firmaKisaAdi || b.firmaAdi || ""),
    "tr"
  )
);

const companyMasterOptions = companyRecords.map((company) => ({
  id: company.id,
  firmaAdi: company.legalName,
  firmaKisaAdi: company.shortName,
  branches: company.branches || []
}));

  const teknoparkOptions = ["all", ...TECHNOPARKS];

  const getProjectBranch = (project) => {
    const company = companyMasterOptions.find((item) => item.id === project.companyId)
      || companyMasterOptions.find((item) => (
        normalizeEntityName(item.firmaAdi) === normalizeEntityName(project.firmaAdi)
        || normalizeEntityName(item.firmaKisaAdi) === normalizeEntityName(project.firmaKisaAdi)
      ));

    return (company?.branches || []).find((branch) => branch.id === project.branchId)
      || (company?.branches || []).find((branch) => (
        normalizeEntityName(branch.technoparkName) === normalizeEntityName(project.teknoparkAdi)
      ))
      || null;
  };

  const getProjectBranchName = (project) => getProjectBranch(project)?.name || project.teknoparkAdi || "";

  const listSourceProjects =
    activeFilter === "tescil"
      ? firmaBazliSonProjeler
      : projects;

    const matchesArchiveSearch = (project) => {
      const search = archivedSearchText.toLowerCase().trim();

      if (!search) return true;

      return (
        project.firmaAdi?.toLowerCase().includes(search) ||
        project.projeAdi?.toLowerCase().includes(search) ||
        getProjectBranchName(project).toLowerCase().includes(search) ||
        project.teknoparkAdi?.toLowerCase().includes(search) ||
        project.projeStbKodu?.toString().toLowerCase().includes(search)
      );
    };

    const filteredArchivedProjects = archivedProjects.filter(matchesArchiveSearch);
    const pendingArchiveProjects = projects
      .filter((project) => project.archiveApprovalStatus === "pending")
      .filter(matchesArchiveSearch);

  const filteredProjects = listSourceProjects.filter((p) => {
  const search = searchText.toLowerCase().trim();

  const matchesSearch =
    !search ||
    p.firmaAdi?.toLowerCase().includes(search) ||
    p.firmaKisaAdi?.toLowerCase().includes(search) ||
    getProjectBranchName(p).toLowerCase().includes(search) ||
    p.teknoparkAdi?.toLowerCase().includes(search) ||
    p.projeAdi?.toLowerCase().includes(search) ||
    p.projeStbKodu?.toString().toLowerCase().includes(search);

  const matchesFilter =
    activeFilter === "all" ||
    (activeFilter === "bitirme" &&
      bitirmeBelgesiAlinacakProjeler.some((x) => x.id === p.id)) ||
    (activeFilter === "tescil" &&
      p.projeKategorisi === "Yazılım" &&
      p.yazilimTescilBelgesi !== "Var") ||
    (activeFilter === "sonlandirma" &&
      sonlandirmaGerekenProjeler.some((x) => x.id === p.id)) ||
    (activeFilter === "yaklasan" &&
      projeBitmesineYakin.some((x) => x.id === p.id)) ||
    (activeFilter === "db" &&
      p.projeStbKodu?.toUpperCase() === "DB");

  const matchesTeknopark =
    selectedTeknopark === "all" || p.teknoparkAdi === selectedTeknopark;

  const matchesCategory =
    categoryFilter === "all" || p.projeKategorisi === categoryFilter;

  const matchesDocument =
    documentFilter === "all" ||
    (documentFilter === "bitirmeVar" && p.projeBitirmeBelgesi === "Var") ||
    (documentFilter === "bitirmeYok" && p.projeBitirmeBelgesi !== "Var") ||
    (documentFilter === "tescilVar" && p.yazilimTescilBelgesi === "Var") ||
    (documentFilter === "tescilYok" && p.yazilimTescilBelgesi !== "Var");

  const kalanGun = getKalanAyNumber(p);

  const matchesDate =
    dateFilter === "all" ||
    (dateFilter === "expired" && kalanGun !== null && kalanGun < 0) ||
    (dateFilter === "0-30" && kalanGun !== null && kalanGun >= 0 && kalanGun <= 30) ||
    (dateFilter === "31-90" && kalanGun !== null && kalanGun > 30 && kalanGun <= 90) ||
    (dateFilter === "90plus" && kalanGun !== null && kalanGun > 90);

  return (
    matchesSearch &&
    matchesFilter &&
    matchesTeknopark &&
    matchesCategory &&
    matchesDocument &&
    matchesDate
  );
});

  const recentFiles = projects
    .flatMap((project) =>
      (project.files || []).map((file) => ({
        ...file,
        firmaAdi: project.firmaAdi,
        projeAdi: project.projeAdi
      }))
    )
    .sort((a, b) => {
      const aDate = new Date(a.uploadedAt || 0);
      const bDate = new Date(b.uploadedAt || 0);

      return bDate - aDate;
    })
    .slice(0, 8);

  const sortedProjects = (() => {
  let data = [...filteredProjects];

  data.sort((a, b) => {
    if (activeFilter === "yaklasan") {
      const kalanCompare =
        getKalanAyNumber(a) - getKalanAyNumber(b);

      if (kalanCompare !== 0) {
        return kalanCompare;
      }
    }

    const teknoparkCompare = getProjectBranchName(a).localeCompare(
      getProjectBranchName(b),
      "tr"
    );

    if (teknoparkCompare !== 0) {
      return teknoparkCompare;
    }

    return (a.firmaKisaAdi || a.firmaAdi || "").localeCompare(
      b.firmaKisaAdi || b.firmaAdi || "",
      "tr"
    );
  });

    if (sortConfig.key) {
      data.sort((a, b) => {
        const dateFields = [
          "baslangicTarihi",
          "bitisTarihi",
          "ekSureTarihi",
          "sonlandirmaTarihi",
          "projeBitirmeBelgesiTarihi",
          "yazilimTescilTarihi"
        ];

        let aValue = a[sortConfig.key] || "";
        let bValue = b[sortConfig.key] || "";

        if (sortConfig.key === "teknoparkAdi") {
          aValue = getProjectBranchName(a);
          bValue = getProjectBranchName(b);
        }

        if (dateFields.includes(sortConfig.key)) {
          const aDate = parseProjectDate(aValue);
          const bDate = parseProjectDate(bValue);

          if (!aDate && !bDate) return 0;
          if (!aDate) return sortConfig.direction === "asc" ? 1 : -1;
          if (!bDate) return sortConfig.direction === "asc" ? -1 : 1;

          return sortConfig.direction === "asc"
            ? aDate - bDate
            : bDate - aDate;
        }

        if (sortConfig.key === "onayliPersonelSayisiToplam") {
          aValue = Number(aValue || 0);
          bValue = Number(bValue || 0);

          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        const result = String(aValue).localeCompare(String(bValue), "tr");

        return sortConfig.direction === "asc" ? result : -result;
      });
    }

    return data;
  })();

  const columns = [
    { label: "Teknopark Şubesi", key: "teknoparkAdi", sortable: true },
    { label: "Firma", key: "firmaAdi", sortable: true },
    { label: "Firma Kısa Adı", key: "firmaKisaAdi", sortable: true },
    { label: "Proje STB Kodu", key: "projeStbKodu", sortable: true },
    { label: "Proje Adı", key: "projeAdi", sortable: true },
    { label: "Proje Süresi", key: "projeSuresi", sortable: false },
    { label: "Kalan Ay Süre", key: "kalanAySure", sortable: false },
    { label: "Başlangıç Tarihi", key: "baslangicTarihi", sortable: true },
    { label: "Bitiş Tarihi", key: "bitisTarihi", sortable: true },
    { label: "Ek Süre Tarihi", key: "ekSureTarihi", sortable: true },
    { label: "Sonlandırma Tarihi", key: "sonlandirmaTarihi", sortable: true },
    { label: "Toplam Personel", key: "onayliPersonelSayisiToplam", sortable: true },
    { label: "Proje Kategorisi", key: "projeKategorisi", sortable: true },
    { label: "Proje Bitirme Belgesi", key: "projeBitirmeBelgesi", sortable: false },
    { label: "Proje Bitirme Belgesi Kodu", key: "projeBitirmeBelgesiKodu", sortable: false },
    { label: "Proje Bitirme Belgesi Tarihi", key: "projeBitirmeBelgesiTarihi", sortable: true },
    { label: "Yazılım Tescil Belgesi", key: "yazilimTescilBelgesi", sortable: false },
    { label: "Yazılım Tescil Tarihi", key: "yazilimTescilTarihi", sortable: true },
    { label: "Açıklama", key: "aciklamaNot", sortable: false }
  ];

  const tableColumns = columns.filter((column) =>
  visibleColumns.includes(column.key)
  );

  const renderCell = (p, key) => {
    switch (key) {
      case "teknoparkAdi": {
        const branch = getProjectBranch(p);
        return (
          <span style={{ display: "grid", gap: 2 }}>
            <strong>{branch?.name || p.teknoparkAdi || "-"}</strong>
            {branch?.technoparkName && <small style={{ color: "#64748b" }}>{branch.technoparkName}</small>}
          </span>
        );
      }

      case "projeSuresi":
        return calculateProjeSuresiForProject(p);

      case "projeAdi":
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, maxWidth: "100%" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.projeAdi || "-"}</span>
            {p.archiveApprovalStatus === "pending" && (
              <span
                style={{
                  flex: "0 0 auto",
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: "#fef3c7",
                  color: "#92400e",
                  fontSize: 10,
                  fontWeight: 900
                }}
              >
                Arşiv Onayı Bekliyor
              </span>
            )}
          </span>
        );

      case "kalanAySure":
        return (
          <span
            style={{
              ...getKalanSureBadgeStyle(p),
              padding: "5px 10px",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 13
            }}
          >
            {calculateKalanAySureForProject(p)}
          </span>
        );

      case "baslangicTarihi":
      case "bitisTarihi":
      case "ekSureTarihi":
      case "sonlandirmaTarihi":
      case "projeBitirmeBelgesiTarihi":
      case "yazilimTescilTarihi":
        return displayDate(p[key]);

      case "projeBitirmeBelgesi":
      case "yazilimTescilBelgesi":
        return (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: 20,
              background: p[key] === "Var" ? "#dcfce7" : "#fee2e2",
              color: p[key] === "Var" ? "green" : "red",
              fontWeight: 600
            }}
          >
            {p[key]}
          </span>
        );

      default:
        return p[key] || "";
    }
  };

  const totalPages = Math.ceil(sortedProjects.length / pageSize);

  const paginatedProjects = sortedProjects.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const exportToExcel = () => {
    if (!isAdmin) {
      alert("Excel'e aktarma işlemi için admin yetkisi gerekir.");
      return;
    }

    const exportData = sortedProjects.map((p) => ({
      "Teknopark Şubesi": getProjectBranchName(p),
      Teknopark: p.teknoparkAdi || "",
      Firma: p.firmaAdi || "",
      "Firma Kısa Adı": p.firmaKisaAdi || "",
      "Ar-Ge Personeli": p.argePersoneli || 0,
      "Yazılım Personeli": p.yazilimPersoneli || 0,
      "Destek Personeli": p.destekPersoneli || 0,
      "Toplam Personel": p.onayliPersonelSayisiToplam || 0,
      "Proje Kategorisi": p.projeKategorisi || "",
      "Proje STB Kodu": p.projeStbKodu || "",
      "Proje Adı": p.projeAdi || "",
      "Başlangıç Tarihi": displayDate(p.baslangicTarihi),
      "Bitiş Tarihi": displayDate(p.bitisTarihi),
      "Ek Süre Tarihi": displayDate(p.ekSureTarihi),
      "Kalan Ay Süre": p.kalanAySure || "",
      "Sonlandırma Tarihi": displayDate(p.sonlandirmaTarihi),
      "Proje Bitirme Belgesi": p.projeBitirmeBelgesi || "",
      "Proje Bitirme Belgesi Kodu": p.projeBitirmeBelgesiKodu || "",
      "Proje Bitirme Belgesi Tarihi": displayDate(p.projeBitirmeBelgesiTarihi),
      "Yazılım Tescil Belgesi": p.yazilimTescilBelgesi || "",
      "Yazılım Tescil Tarihi": displayDate(p.yazilimTescilTarihi),
      Açıklama: p.aciklamaNot || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Projeler");
    XLSX.writeFile(workbook, "Proje_Takip_Listesi.xlsx");
  };

  const handleExcelUpload = async (e) => {
  if (!isAdmin) {
    alert("Excel yükleme işlemi için admin yetkisi gerekir.");
    e.target.value = "";
    return;
  }

  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();

  reader.onload = async (evt) => {
    try {
    const data = evt.target.result;
    const workbook = XLSX.read(data, { type: "array", cellDates: true });

    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

    if (!jsonData.length) {
      alert("Excel dosyasında aktarılacak proje satırı bulunamadı.");
      return;
    }

    let eklenenProjeSayisi = 0;
    let guncellenenProjeSayisi = 0;

    for (const row of jsonData) {
      const getValue = (headers) => getExcelValue(row, headers);

      const projeStbKodu = String(
        getValue(["Proje STB Kodu", "STB Proje Kodu", "STB Kodu"]) || ""
      ).trim();

      const projeBitirmeBelgesiKodu = getValue([
        "Proje Bitirme Belgesi Kodu",
        "Proje Bitirme Bakanlık Kodu",
        "Bitirme Belgesi Kodu",
        "Bitirme Bakanlık Kodu"
      ]);
      const projeBitirmeBelgesiTarihi = formatExcelDate(
        getValue([
          "Proje Bitirme Belgesi Tarihi",
          "Bitirme Belgesi Tarihi"
        ])
      );
      const projeBitirmeBelgesi = normalizeDocumentStatus(
        getValue([
          "Proje Bitirme Belgesi",
          "Bitirme Belgesi Var mı?",
          "Bitirme Belgesi Var mı"
        ]),
        Boolean(projeBitirmeBelgesiKodu || projeBitirmeBelgesiTarihi)
      );

      const yazilimTescilTarihi = formatExcelDate(
        getValue(["Yazılım Tescil Tarihi", "Tescil Belgesi Tarihi"])
      );
      const yazilimTescilBelgesi = normalizeDocumentStatus(
        getValue([
          "Yazılım Tescil Belgesi",
          "Tescil Belgesi Var mı?",
          "Tescil Belgesi Var mı"
        ]),
        Boolean(yazilimTescilTarihi)
      );

      const argePersoneli = Number(
        getValue(["Ar-Ge Personeli", "Araştırmacı Personel"]) || 0
      );
      const yazilimPersoneli = Number(getValue(["Yazılım Personeli"]) || 0);
      const destekPersoneli = Number(getValue(["Destek Personeli"]) || 0);

      const projectData = {
        firmaAdi: getValue(["Firma Adı"]) || "",
        firmaKisaAdi:
          getValue([
            "Firma Kısa Adı",
            "Firma Kisa Adi",
            "Firma Kısa Ad",
            "Firma Kisa Ad",
            "Firma Kısa"
          ]) ||
          "",
        teknoparkAdi: getValue(["Teknopark Adı"]) || "",
        projeStbKodu,
        projeAdi: getValue(["Proje Adı"]) || "",
        projeKategorisi: getValue(["Proje Kategorisi"]) || "",
        baslangicTarihi: formatExcelDate(
          getValue(["Proje Baş. Tarihi", "Başlangıç Tarihi"])
        ),
        bitisTarihi: formatExcelDate(
          getValue(["Proje Bitiş Tarihi", "Bitiş Tarihi"])
        ),
        ekSureTarihi: formatExcelDate(getValue(["Ek Süre Tarihi"])),
        sonlandirmaTarihi: formatExcelDate(
          getValue(["Sonlandırma Tarihi"])
        ),
        argePersoneli,
        yazilimPersoneli,
        destekPersoneli,
        onayliPersonelSayisiToplam:
          argePersoneli + yazilimPersoneli + destekPersoneli,
        projeBitirmeBelgesi,
        projeBitirmeBelgesiKodu:
          projeBitirmeBelgesi === "Var"
            ? projeBitirmeBelgesiKodu || ""
            : "",
        projeBitirmeBelgesiTarihi:
          projeBitirmeBelgesi === "Var"
            ? projeBitirmeBelgesiTarihi
            : "",
        yazilimTescilBelgesi,
        yazilimTescilTarihi:
          yazilimTescilBelgesi === "Var"
            ? yazilimTescilTarihi
            : "",
        updatedAt: new Date()
      };

      if (projeStbKodu) {
        const existingProject =
          projeStbKodu.toLocaleUpperCase("tr-TR") === "DB"
            ? await getProjectBySTBCompanyAndName({
                stbCode: projeStbKodu,
                companyName: projectData.firmaAdi,
                projectName: projectData.projeAdi
              })
            : await getProjectBySTB(projeStbKodu);

          if (existingProject) {
            await updateProjectById(existingProject.id, projectData);
            guncellenenProjeSayisi += 1;
          } else {
            await createProject(projectData);
            eklenenProjeSayisi += 1;
          }

      } else {
        await createProject(projectData);

      eklenenProjeSayisi += 1;
      }
    }

    await addActivityLog(
      "EXCEL_YUKLENDI",
      `${guncellenenProjeSayisi} proje güncellendi, ${eklenenProjeSayisi} proje eklendi.`
    );

    if (guncellenenProjeSayisi > 0 || eklenenProjeSayisi > 0) {
      await notifyTeam({
        title: "Projeler Excel ile güncellendi",
        body: `${getCurrentUserDisplayName()}, Excel ile ${eklenenProjeSayisi} proje ekledi, ${guncellenenProjeSayisi} proje güncelledi.`,
        type: "project",
        link: "projects"
      });
    }

    alert(
      `Excel başarıyla yüklendi. ${guncellenenProjeSayisi} proje güncellendi, ${eklenenProjeSayisi} proje eklendi.`
    );
    } catch (error) {
      console.error("Excel yükleme hatası:", error);
      alert(`Excel yüklenemedi: ${error.message || "Bilinmeyen hata"}`);
    } finally {
    e.target.value = "";
    }
  };

  reader.onerror = () => {
    alert("Excel dosyası okunamadı. Lütfen dosyayı kapatıp tekrar deneyin.");
    e.target.value = "";
  };

  reader.readAsArrayBuffer(file);
};

  if (!canView) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: themeMode === "dark" ? "#0f172a" : "#f5f7fb",
          color: "#991b1b",
          fontWeight: 700
        }}
      >
        Bu kullanıcı için görüntüleme yetkisi tanımlanmamış.
      </div>
    );
  }

  const onlineUserCount = presenceUsers.filter(
    (presence) => (presence.uid || presence.id) !== user?.uid && isPresenceOnline(presence)
  ).length;
  const totalNotificationCount = userNotifications.length + (isAdmin ? adminNotifications.length : 0);
  const unreadNotificationCount =
    userNotifications.filter((notification) => !notification.isRead).length +
    (isAdmin ? adminNotifications.filter((notification) => !notification.isRead).length : 0);

  const renderProcessWorkspaceTabs = () => {
    if (!visibleProcessProjects.length && !canEdit) return null;

    const isDarkTheme = themeMode === "dark";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
          padding: "8px 10px",
          borderRadius: 16,
          background: isDarkTheme ? "#122033" : "#ffffff",
          border: isDarkTheme ? "1px solid #263b55" : "1px solid #e2e8f0",
          boxShadow: isDarkTheme ? "none" : "0 8px 24px rgba(15,23,42,0.06)"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            overflowX: "auto",
            paddingBottom: 2,
            flex: 1
          }}
        >
          {visibleProcessProjects.map((project) => {
            const isSelectedProject = activeTab === "monthlyProcesses" && project.id === selectedProcessProjectId;

            return (
              <button
                key={project.id}
                type="button"
                onClick={() => openProcessProject(project.id)}
                title={project.name}
                style={{
                  border: isSelectedProject
                    ? "1px solid transparent"
                    : isDarkTheme
                    ? "1px solid #2c3d55"
                    : "1px solid #dbe2ea",
                  background: isSelectedProject
                    ? "linear-gradient(135deg, #124254, #0f766e)"
                    : isDarkTheme
                    ? "#0f172a"
                    : "#f8fafc",
                  color: isSelectedProject ? "#fff" : isDarkTheme ? "#d7e3f1" : "#124254",
                  padding: "9px 13px",
                  borderRadius: 999,
                  fontWeight: 950,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: isSelectedProject ? "0 8px 18px rgba(18,66,84,0.18)" : "none"
                }}
              >
                {project.name}
              </button>
            );
          })}

          {canEdit && (
            <button
              type="button"
              onClick={openMailTasks}
              style={{
                border: activeTab === "mailTasks" ? "1px solid transparent" : isDarkTheme ? "1px solid #2c3d55" : "1px solid #dbe2ea",
                background: activeTab === "mailTasks"
                  ? "linear-gradient(135deg, #124254, #0f766e)"
                  : isDarkTheme
                  ? "#0f172a"
                  : "#f8fafc",
                color: activeTab === "mailTasks" ? "#fff" : isDarkTheme ? "#d7e3f1" : "#124254",
                padding: "9px 13px",
                borderRadius: 999,
                fontWeight: 950,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: activeTab === "mailTasks" ? "0 8px 18px rgba(18,66,84,0.18)" : "none"
              }}
            >
              Mail Görevleri
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isManagementHostname()) {
    return (
      <>
        <AppDialogProvider />
        <SupportManagementDashboard
          user={user}
          userData={userData}
          users={users}
          onLogout={onLogout}
          notifyTeam={notifyTeam}
          actorName={getCurrentUserDisplayName()}
        />
      </>
    );
  }

  return (
    <div
      className={isTenantPortal ? "aurora-portal-shell" : "legacy-portal-shell"}
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "var(--app-bg)"
      }}
    >
        <AppDialogProvider />

        {!isTenantPortal && <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          canEdit={canEdit}
          isAdmin={isAdmin}
          canAssignCompanies={canAssignCompanies}
          canMonitorBackups={canMonitorBackups}
          canManageParasut={canManageParasut}
          onLogout={onLogout}
          user={user}
          userData={userData}
          userRole={userRole}
          moduleSettings={moduleSettings}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          themeMode={themeMode}
          toggleThemeMode={toggleThemeMode}
          processProjects={visibleProcessProjects}
          selectedProcessProjectId={selectedProcessProjectId}
          openProcessProject={openProcessProject}
          createNewProcessProject={createNewProcessProject}
          renameProcessProject={renameProcessProject}
          archiveProcessProject={archiveProcessProject}
          moveProcessProject={moveProcessProject}
          isTenantPortal={false}
        />}

        {isTenantPortal && (
          <CustomerNavigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            canAssignCompanies={canAssignCompanies}
            canMonitorBackups={canMonitorBackups}
            isAdmin={isAdmin}
            onLogout={onLogout}
            user={user}
            userData={userData}
            themeMode={themeMode}
            toggleThemeMode={toggleThemeMode}
            selectedProcessProjectId={selectedProcessProjectId}
            openProcessProject={openProcessProject}
          />
        )}

      <div
        className={isTenantPortal ? "aurora-portal-content" : "legacy-portal-content"}
        style={{
          flex: 1,
          boxSizing: "border-box",
          padding: isTenantPortal ? "28px clamp(18px, 4vw, 58px) 126px" : "40px 48px",
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          background: "var(--app-bg)",
          color: themeMode === "dark" ? "#e5edf6" : "inherit",
          minHeight: "100vh"
        }}
      >
        {!canEdit && activeTab !== "leaveManagement" && activeTab !== "teknoparkAI" && (
          <div
            style={{
              marginBottom: 20,
              padding: "12px 16px",
              borderRadius: 12,
              background: "#eef4ff",
              border: "1px solid #bfdbfe",
              color: "#124254",
              fontWeight: 600,
              fontSize: 14
            }}
          >
            Görüntüleme yetkisi ile giriş yaptınız. Ekleme, güncelleme ve silme işlemleri kapalıdır.
          </div>
        )}

	          {activeTab === "dashboard" && (
	            <>
	              <DashboardOverview
                projects={projects}
                degerlendirmeBekleyenProjeler={degerlendirmeBekleyenProjeler}
                projeBitmesineYakin={projeBitmesineYakin}
                sonlandirmaGerekenProjeler={sonlandirmaGerekenProjeler}
                bitirmeBelgesiAlinacakProjeler={bitirmeBelgesiAlinacakProjeler}
                yazilimTescilGerekenProjeler={yazilimTescilGerekenProjeler}
                toplamPersonel={toplamPersonel}
                suresiGecenProjeler={suresiGecenProjeler}
                setActiveFilter={setActiveFilter}
                setActiveTab={setActiveTab}
                setCurrentPage={setCurrentPage}
                setDateFilter={setDateFilter}
                setShowNewProjectModal={setShowNewProjectModal}
                setSelectedProject={setSelectedProject}
                setSelectedCompany={setSelectedCompany}
                setEditProject={setEditProject}
                getProjectStatusBadge={getProjectStatusBadge}
                cardHoverStyle={cardHoverStyle}
                firmaListesi={firmaListesi}
                activityLogs={activityLogs}
                setCompanyFilter={setCompanyFilter}
                recentFiles={recentFiles}
                themeMode={themeMode}
                isAdmin={isAdmin}
              />

	            </>
	          )}

	          {activeTab === "announcements" && (
	            <AnnouncementsView
	              announcements={announcements}
	              isAdmin={isAdmin}
	              themeMode={themeMode}
	              onCreateAnnouncement={publishAnnouncement}
	            />
	          )}

	          {activeTab === "support" && (
	            <SupportCenterView
	              user={user}
	              userData={userData}
	              users={users}
	              themeMode={themeMode}
	              initialTicketId={supportTicketToOpen}
	              onInitialTicketHandled={() => setSupportTicketToOpen("")}
	            />
	          )}

	          {activeTab === "teknoparkAI" && isCustomerPortalHostname() && (
	            <TeknoparkAIView
	              tenantId={resolveTenantId(userData)}
	              user={user}
	              themeMode={themeMode}
	              onOpenSupport={(ticketId = "") => {
	                setSupportTicketToOpen(
	                  typeof ticketId === "string" ? ticketId : ""
	                );
	                setActiveTab("support");
	              }}
	            />
	          )}

	          {activeTab === "projects" && (
            <>
              <ProjectsTable
              canEdit={canEdit}
              setShowNewProjectModal={setShowNewProjectModal}
              canImportExport={isAdmin}
              exportToExcel={exportToExcel}
              handleExcelUpload={handleExcelUpload}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
              setCurrentPage={setCurrentPage}
              selectedTeknopark={selectedTeknopark}
              setSelectedTeknopark={setSelectedTeknopark}
              teknoparkOptions={teknoparkOptions}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              documentFilter={documentFilter}
              setDocumentFilter={setDocumentFilter}
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              columns={columns}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              searchText={searchText}
              setSearchText={setSearchText}
              tableColumns={tableColumns}
              paginatedProjects={paginatedProjects}
              sortConfig={sortConfig}
              setSortConfig={setSortConfig}
              renderCell={renderCell}
              sonlandirmaGerekenProjeler={sonlandirmaGerekenProjeler}
              projeBitmesineYakin={projeBitmesineYakin}
              setSelectedProject={setSelectedProject}
              setEditProject={setEditProject}
              currentPage={currentPage}
              totalPages={totalPages}
              sortedProjects={sortedProjects}
              themeMode={themeMode}
            />
            </>
          )}

          {activeTab === "monthlyProcesses" && moduleSettings.monthlyProcesses && (
            isCustomerPortalHostname() ? (
              <TenantMonthlyProcessesView
                user={{ ...(userData || {}), uid: user?.uid, email: user?.email }}
                userData={userData}
                firmaListesi={customerCompanyOptions.length ? customerCompanyOptions : firmaListesi}
                themeMode={themeMode}
              />
            ) : (
              <MonthlyProcessesView
                firmaListesi={customerCompanyOptions.length ? customerCompanyOptions : firmaListesi}
                user={{ ...(userData || {}), uid: user?.uid, email: user?.email }}
                users={users}
                processProject={selectedProcessProject}
                processProjects={visibleProcessProjects}
                selectProcessProject={openProcessProject}
                createNewProcessProject={createNewProcessProject}
                renameProcessProject={renameProcessProject}
                archiveProcessProject={archiveProcessProject}
                moveProcessProject={moveProcessProject}
                addActivityLog={addActivityLog}
                openMailTasks={openMailTasks}
                themeMode={themeMode}
              />
            )
          )}

          {activeTab === "mailTasks" && canEdit && (
            <>
              {renderProcessWorkspaceTabs()}
              <MailTasksView
                user={{ ...(userData || {}), uid: user?.uid, email: user?.email }}
                users={users}
                firmaListesi={customerCompanyOptions.length ? customerCompanyOptions : firmaListesi}
                processProjects={visibleProcessProjects}
                isAdmin={isAdmin}
                themeMode={themeMode}
                addActivityLog={addActivityLog}
                notifyTeam={notifyTeam}
              />
            </>
          )}

          {(activeTab === "profile" || activeTab === "leaveManagement") && (
            <ProfileView
              user={{ ...(userData || {}), uid: user?.uid, email: user?.email }}
              userData={userData}
              users={users}
              isAdmin={isAdmin}
              onUserDataUpdate={onUserDataUpdate}
              onLogout={onLogout}
              themeMode={themeMode}
            />
          )}

          {activeTab === "portalCredentials" && (
            <PortalCredentialsView
              credentialScope={{ kind: "tenant", tenantId: resolveTenantId(userData) }}
              notifyTeam={notifyTeam}
              actorName={getCurrentUserDisplayName()}
            />
          )}

          {activeTab === "parasutIntegration" && canManageParasut && (
            <ParasutIntegrationView themeMode={themeMode} firmaListesi={firmaListesi} />
          )}

          {activeTab === "files" && (
            <FilesView
              projects={projects}
              companyContacts={companyContacts}
              setPreviewFile={setPreviewFile}
              canEdit={canEdit}
              deleteProjectFileFromFilesView={deleteProjectFileFromFilesView}
              setSelectedProject={setSelectedProject}
              setEditProject={setEditProject}
            />
          )}

          {activeTab === "exemptionDayReport" && (
            <ExemptionDayReportView
              projects={projects}
              themeMode={themeMode}
            />
          )}

          {activeTab === "postDeclaration5746Check" && (
            <PostDeclaration5746CheckView themeMode={themeMode} />
          )}

          {activeTab === "monthlyFinancialReports" && isCustomerPortalHostname() && (
            <MonthlyFinancialReportsView
              tenantId={resolveTenantId(userData)}
              user={user}
              companyOptions={companyMasterOptions.length ? companyMasterOptions : (customerCompanyOptions.length ? customerCompanyOptions : firmaListesi)}
              themeMode={themeMode}
              canEdit={canEdit}
            />
          )}

          {activeTab === "annualTaxObligations" && isCustomerPortalHostname() && (
            <AnnualTaxObligationsView
              tenantId={resolveTenantId(userData)}
              user={user}
              userData={userData}
              companyOptions={companyMasterOptions.length ? companyMasterOptions : (customerCompanyOptions.length ? customerCompanyOptions : firmaListesi)}
              themeMode={themeMode}
              canEdit={canEdit}
              notifyTeam={notifyTeam}
            />
          )}


          {showNewProjectModal && canEdit && (
            <NewProjectModal
              form={form}
              updateField={updateField}
              formInputStyle={formInputStyle}
              calculateTotalPersonel={calculateTotalPersonel}
              calculateKalanAySure={calculateKalanAySure}
              saveProject={saveProject}
              setShowNewProjectModal={setShowNewProjectModal}
              companyOptions={companyMasterOptions.length ? companyMasterOptions : (customerCompanyOptions.length ? customerCompanyOptions : firmaListesi)}
              technoparkOptions={teknoparkOptions.filter((option) => option !== "all")}
            />
          )}

          {selectedCompany && (
            <CompanyDetailModal
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              setSelectedProject={setSelectedProject}
              setEditProject={setEditProject}
              getKalanAyNumber={getKalanAyNumber}
              calculateKalanAySureForProject={calculateKalanAySureForProject}
            />
          )}

          {selectedProject && editProject && (
            <ProjectDetailModal
            selectedProject={selectedProject}
            editProject={editProject}
            setEditProject={setEditProject}
            setSelectedProject={setSelectedProject}
            canEdit={canEdit}
            detailInputStyle={detailInputStyle}
            readOnlyInputStyle={readOnlyInputStyle}
            updateProject={updateProject}
            deleteProject={requestProjectArchive}
            uploadProjectFile={uploadProjectFile}
            deleteProjectFile={deleteProjectFile}
            fileCategory={fileCategory}
            setFileCategory={setFileCategory}
            calculateKalanAySureForProject={calculateKalanAySureForProject}
            calculateProjeSuresiForProject={calculateProjeSuresiForProject}
            displayDate={displayDate}
            dateInputValue={dateInputValue}
            setPreviewFile={setPreviewFile}
            uploadProgress={uploadProgress}
            uploadingFileName={uploadingFileName}
            uploadTotalCount={uploadTotalCount}
            uploadedCount={uploadedCount}
            isDraggingFile={isDraggingFile}
            setIsDraggingFile={setIsDraggingFile}
            isUploadingFile={isUploadingFile}
            processProjects={processProjects}
            companyOptions={companyMasterOptions}
            currentUserEmail={user?.email || ""}
            isAdmin={isAdmin}
          />
          )}

          {previewFile && (
            <FilePreviewModal
              file={previewFile}
              onClose={() => setPreviewFile(null)}
            />
          )}

          {activeTab === "companies" && (
            <CompaniesView
              companies={companyRecords}
              projects={projects}
              contacts={companyContacts}
              technoparkOptions={teknoparkOptions.filter((option) => option !== "all")}
              canEdit={canEdit}
              onContactsChange={loadCompanyContacts}
              themeMode={themeMode}
            />
          )} 

          {activeTab === "archived" && (
            <ArchivedProjectsView
              pendingArchiveProjects={pendingArchiveProjects}
              filteredArchivedProjects={filteredArchivedProjects}
              archivedSearchText={archivedSearchText}
              setArchivedSearchText={setArchivedSearchText}
              isAdmin={isAdmin}
              approveProjectArchive={approveProjectArchive}
              rejectProjectArchive={rejectProjectArchive}
              currentUserEmail={user?.email || ""}
              themeMode={themeMode}
            />
          )}        

          {activeTab === "logs" && isAdmin && (
            <LogsView activityLogs={activityLogs} themeMode={themeMode} />
          )}        

          {activeTab === "backupStatus" && canMonitorBackups && (
            <BackupStatusView
              themeMode={themeMode}
              currentUserEmail={user?.email || ""}
            />
          )}        

          {activeTab === "users" && canAssignCompanies && (
            <UsersView
              users={users}
              userForm={userForm}
              setUserForm={setUserForm}
              saveUserPermission={saveUserPermission}
              removeUserPermission={removeUserPermission}
              currentUserEmail={user?.email}
              canGrantParasutAccess={isParasutOwner}
              firmaListesi={firmaListesi}
              processProjects={processProjects}
              updateUserPermission={updateUserPermission}
              updateUserCompanyAssignmentsOnly={updateUserCompanyAssignmentsOnly}
              sendPasswordResetToUser={sendPasswordResetToUser}
              generatePasswordResetLinkForUser={generatePasswordResetLinkForUser}
              isAdmin={isAdmin}
              canAssignCompanies={canAssignCompanies}
              themeMode={themeMode}
            />
          )}

	      </div>

	      {popupAnnouncement && (
	        <AnnouncementsView
	          announcements={announcements}
	          isAdmin={isAdmin}
	          themeMode={themeMode}
	          popupAnnouncement={popupAnnouncement}
	          popupOnly
	          onClosePopup={() => dismissAnnouncementPopup()}
	          onOpenAnnouncements={() => {
	            dismissAnnouncementPopup();
	            setActiveTab("announcements");
	          }}
	        />
	      )}

      {isMessagesPanelOpen && (
        <div
          onClick={() => setIsMessagesPanelOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(15,23,42,0.18)",
            backdropFilter: "blur(1px)"
          }}
        >
          <div
            ref={messagesPanelRef}
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "absolute",
              top: 24,
              right: 24,
              bottom: 24,
              width: "min(920px, calc(100vw - 220px))",
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 24,
              background: themeMode === "dark" ? "#0f172a" : "#f8fafc",
              border: `1px solid ${themeMode === "dark" ? "#27364d" : "#dbe4ef"}`,
              boxShadow: "0 30px 90px rgba(15,23,42,0.28)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                borderBottom: `1px solid ${themeMode === "dark" ? "#27364d" : "#e2e8f0"}`,
                background: themeMode === "dark" ? "#162235" : "#fff"
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontWeight: 950,
                  color: themeMode === "dark" ? "#e5edf6" : "#0f172a"
                }}
              >
                <MessageCircle size={20} />
                Mesajlar
                {onlineUserCount > 0 && (
                  <span
                    style={{
                      padding: "4px 8px",
                      borderRadius: 999,
                      background: "#dcfce7",
                      color: "#166534",
                      fontSize: 12,
                      fontWeight: 900
                    }}
                  >
                    {onlineUserCount} çevrimiçi
                  </span>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: 4,
                    borderRadius: 12,
                    border: `1px solid ${themeMode === "dark" ? "#334155" : "#dbe4ef"}`,
                    background: themeMode === "dark" ? "#0f172a" : "#f8fafc"
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setMessageFontSize((current) => Math.max(8, current - 1))
                    }
                    title="Mesaj puntosunu küçült"
                    style={{
                      minWidth: 34,
                      height: 28,
                      border: "none",
                      borderRadius: 9,
                      background: themeMode === "dark" ? "#1e293b" : "#e2e8f0",
                      color: themeMode === "dark" ? "#e5edf6" : "#0f172a",
                      fontWeight: 950,
                      cursor: "pointer"
                    }}
                  >
                    A-
                  </button>

                  <span
                    style={{
                      minWidth: 44,
                      textAlign: "center",
                      color: themeMode === "dark" ? "#b6c4d8" : "#475569",
                      fontSize: 12,
                      fontWeight: 900
                    }}
                  >
                    {messageFontSize}px
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setMessageFontSize((current) => Math.min(18, current + 1))
                    }
                    title="Mesaj puntosunu büyüt"
                    style={{
                      minWidth: 34,
                      height: 28,
                      border: "none",
                      borderRadius: 9,
                      background: themeMode === "dark" ? "#1e293b" : "#e2e8f0",
                      color: themeMode === "dark" ? "#e5edf6" : "#0f172a",
                      fontWeight: 950,
                      cursor: "pointer"
                    }}
                  >
                    A+
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setIsMessagesPanelOpen(false)}
                  title="Mesaj panelini kapat"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 12,
                    border: `1px solid ${themeMode === "dark" ? "#334155" : "#dbe4ef"}`,
                    background: themeMode === "dark" ? "#0f172a" : "#f8fafc",
                    color: themeMode === "dark" ? "#e5edf6" : "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer"
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
              <MessagesView
                currentUser={{ ...(userData || {}), uid: user?.uid, email: user?.email }}
                users={users}
                presenceUsers={presenceUsers}
                themeMode={themeMode}
                messageFontSize={messageFontSize}
                compact
              />
            </div>
          </div>
        </div>
      )}

      {messageNotification && !isMessagesPanelOpen && (
        <button
          type="button"
          onClick={openMessagesPanel}
          style={{
            position: "fixed",
            right: 28,
            bottom: 102,
            zIndex: 1900,
            width: 340,
            maxWidth: "calc(100vw - 56px)",
            padding: 0,
            border: "none",
            borderRadius: 18,
            background: themeMode === "dark" ? "#162235" : "#fff",
            color: themeMode === "dark" ? "#e5edf6" : "#0f172a",
            boxShadow: "0 22px 50px rgba(15,23,42,0.24)",
            cursor: "pointer",
            overflow: "hidden",
            textAlign: "left"
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              padding: "14px 15px",
              border: `1px solid ${themeMode === "dark" ? "#27364d" : "#dbe4ef"}`,
              borderRadius: 18
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #2563eb, #0891b2)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <MessageCircle size={19} />
            </span>

            <span style={{ minWidth: 0, flex: 1 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 950,
                  marginBottom: 5,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis"
                }}
              >
                {messageNotification.title}
              </span>

              <span
                style={{
                  display: "block",
                  color: themeMode === "dark" ? "#b6c4d8" : "#475569",
                  fontSize: 13,
                  fontWeight: 700,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis"
                }}
              >
                {messageNotification.body}
              </span>
            </span>
          </div>
        </button>
      )}

      {isNotificationsPanelOpen && (
        <div
          ref={notificationPanelRef}
          style={{
            position: "fixed",
            right: 28,
            bottom: 168,
            zIndex: 1950,
            width: 390,
            maxWidth: "calc(100vw - 56px)",
            maxHeight: "62vh",
            overflow: "hidden",
            borderRadius: 20,
            background: themeMode === "dark" ? "#162235" : "#fff",
            color: themeMode === "dark" ? "#e5edf6" : "#0f172a",
            border: `1px solid ${themeMode === "dark" ? "#27364d" : "#dbe4ef"}`,
            boxShadow: "0 24px 70px rgba(15,23,42,0.28)"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 16px",
              borderBottom: `1px solid ${themeMode === "dark" ? "#27364d" : "#e5e7eb"}`
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 950 }}>Bildirimler</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: themeMode === "dark" ? "#94a3b8" : "#64748b", marginTop: 3 }}>
                {unreadNotificationCount} okunmamış / {totalNotificationCount} toplam bildirim
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {unreadNotificationCount > 0 && (
                <button
                  onClick={markAllNotificationsRead}
                  style={{
                    border: `1px solid ${themeMode === "dark" ? "#2c3d55" : "#dbe4ef"}`,
                    borderRadius: 10,
                    background: themeMode === "dark" ? "#101827" : "#f8fafc",
                    color: themeMode === "dark" ? "#dbeafe" : "#124254",
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 950,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  Tümü okundu
                </button>
              )}
              <button
                onClick={() => setIsNotificationsPanelOpen(false)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: `1px solid ${themeMode === "dark" ? "#2c3d55" : "#e5e7eb"}`,
                  background: themeMode === "dark" ? "#101827" : "#f8fafc",
                  color: themeMode === "dark" ? "#e5edf6" : "#0f172a",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div style={{ maxHeight: "calc(62vh - 68px)", overflowY: "auto", padding: 10, display: "grid", gap: 8 }}>
            {totalNotificationCount === 0 && (
              <div style={{ padding: 18, textAlign: "center", color: themeMode === "dark" ? "#94a3b8" : "#64748b", fontWeight: 800 }}>
                Bildirim yok.
              </div>
            )}

            {userNotifications.map((notification) => {
              const isMentionNotification = notification.type === "mention";
              const notificationBackground = notification.isRead
                ? (themeMode === "dark" ? "#111827" : "#f8fafc")
                : isMentionNotification
                  ? (themeMode === "dark" ? "#312e81" : "#eef2ff")
                  : (themeMode === "dark" ? "#102a3d" : "#eff6ff");
              const notificationBorder = notification.isRead
                ? (themeMode === "dark" ? "1px solid #27364d" : "1px solid #e2e8f0")
                : isMentionNotification
                  ? (themeMode === "dark" ? "1px solid #818cf8" : "1px solid #818cf8")
                  : (themeMode === "dark" ? "1px solid #1d4f72" : "1px solid #bfdbfe");
              const notificationColor = notification.isRead
                ? (themeMode === "dark" ? "#94a3b8" : "#475569")
                : isMentionNotification
                  ? (themeMode === "dark" ? "#eef2ff" : "#3730a3")
                  : (themeMode === "dark" ? "#dbeafe" : "#1e3a8a");

              return (
              <div
                key={notification.id}
                style={{
                  padding: 12,
                  borderRadius: 15,
                  background: notificationBackground,
                  border: notificationBorder,
                  color: notificationColor,
                  opacity: notification.isRead ? 0.82 : 1,
                  boxShadow: !notification.isRead && isMentionNotification
                    ? (themeMode === "dark" ? "0 0 0 1px rgba(129,140,248,0.18)" : "0 8px 18px rgba(99,102,241,0.12)")
                    : "none"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 950 }}>
                    {isMentionNotification && !notification.isRead ? "@ " : ""}{notification.title}
                  </div>
                  {notification.isRead && (
                    <span style={{ fontSize: 11, fontWeight: 950, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>
                      Okundu
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, fontWeight: 750, lineHeight: 1.45, marginTop: 5 }}>
                  {notification.body}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                  <button
                    onClick={() => markTaskNotificationRead(notification)}
                    disabled={notification.isRead}
                    style={{
                      border: themeMode === "dark" ? "1px solid #2c3d55" : "1px solid #bfdbfe",
                      borderRadius: 9,
                      padding: "7px 10px",
                      background: themeMode === "dark" ? "#172234" : "#fff",
                      color: themeMode === "dark" ? "#dbeafe" : "#1e3a8a",
                      fontWeight: 900,
                      cursor: notification.isRead ? "default" : "pointer",
                      opacity: notification.isRead ? 0.65 : 1
                    }}
                  >
                    Okundu
                  </button>
                  <button
                    onClick={() => openTaskNotification(notification)}
                    style={{
                      border: "none",
                      borderRadius: 9,
                      padding: "7px 10px",
                      background: "#2563eb",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Aç
                  </button>
                </div>
              </div>
              );
            })}

            {isAdmin && adminNotifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  padding: 12,
                  borderRadius: 15,
                  background: notification.isRead
                    ? (themeMode === "dark" ? "#111827" : "#f8fafc")
                    : (themeMode === "dark" ? "#332916" : "#fff7ed"),
                  border: notification.isRead
                    ? (themeMode === "dark" ? "1px solid #27364d" : "1px solid #e2e8f0")
                    : (themeMode === "dark" ? "1px solid #7c5b18" : "1px solid #fed7aa"),
                  color: notification.isRead
                    ? (themeMode === "dark" ? "#94a3b8" : "#475569")
                    : (themeMode === "dark" ? "#fdba74" : "#9a3412"),
                  opacity: notification.isRead ? 0.82 : 1
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 950 }}>{notification.title}</div>
                  {notification.isRead && (
                    <span style={{ fontSize: 11, fontWeight: 950, color: themeMode === "dark" ? "#94a3b8" : "#64748b" }}>
                      Okundu
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, fontWeight: 750, lineHeight: 1.45, marginTop: 5 }}>
                  {notification.body}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <button
                    onClick={() => openAdminNotification(notification)}
                    style={{
                      border: "none",
                      borderRadius: 9,
                      padding: "7px 10px",
                      background: "#124254",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Aç
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        className="aurora-notification-fab"
        ref={notificationButtonRef}
        type="button"
        onClick={() => {
          setIsNotificationsPanelOpen((current) => !current);
          setIsMessagesPanelOpen(false);
        }}
        title="Bildirimler"
        style={{
          position: "fixed",
          right: 28,
          bottom: 98,
          zIndex: 1900,
          width: 54,
          height: 54,
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg, #f59e0b, #ef4444)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 18px 36px rgba(239,68,68,0.28)"
        }}
      >
        <Bell size={23} />
        {unreadNotificationCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -5,
              right: -5,
              minWidth: 24,
              height: 24,
              padding: "0 7px",
              borderRadius: 999,
              background: "#ef4444",
              color: "#fff",
              border: "2px solid #fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 950
            }}
          >
            {unreadNotificationCount}
          </span>
        )}
      </button>

      <button
        className="aurora-message-fab"
        type="button"
        onClick={openMessagesPanel}
        title="Mesajlar"
        style={{
          position: "fixed",
          right: 28,
          bottom: 28,
          zIndex: 1900,
          width: 62,
          height: 62,
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg, #2563eb, #0891b2)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 18px 36px rgba(37,99,235,0.32)"
        }}
      >
        <MessageCircle size={26} />
        {unreadMessageCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -5,
              right: -5,
              minWidth: 24,
              height: 24,
              padding: "0 7px",
              borderRadius: 999,
              background: "#ef4444",
              color: "#fff",
              border: "2px solid #fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 950
            }}
          >
            {unreadMessageCount}
          </span>
        )}
        {unreadMessageCount === 0 && onlineUserCount > 0 && (
          <span
            style={{
              position: "absolute",
              right: 2,
              bottom: 3,
              width: 15,
              height: 15,
              borderRadius: "50%",
              background: "#22c55e",
              border: "3px solid #fff"
            }}
          />
        )}
      </button>
    </div>
  );
}

export default Dashboard;
